import React, { useState, useEffect, useMemo } from "react";
import { iframeEmbedCode } from "@/lib/chatbot-embed";
import { isChatModel } from "@/lib/ai-model-capabilities";
import { SharePointDocumentSource } from "@/components/sharepoint-document-source";
import { useRoute, useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Bot, Globe, FileText, MessageSquare, Trash2,
  Plus, Copy, Check, Upload, Link as LinkIcon, Code, Code2, Eye, Settings, Palette, HelpCircle,
  Star, Shield, Zap, Layers, Send, User, Radio, Mail, Smartphone, Monitor, BarChart3,
  Download, Clock, TrendingUp, Webhook, ChevronRight, BookOpen, Activity, Rocket, Headphones, X,
  Slack, Hash, MessageCircle, ExternalLink, Plug, ShieldCheck,
  Sparkles, Smile, Volume2, RefreshCw, Type, Building2, Library, FlaskConical,
  type LucideIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { apiRequest, getAuthToken, queryClient, friendlyErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth, hasPermission } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import type { Chatbot, KnowledgeSource } from "@shared/schema";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChatPreview from "@/components/chat-preview";
import { RichTextEditor } from "@/components/rich-text-editor";
import TurndownService from "turndown";
import { BOT_THEMES, getThemeById, resolveTheme, resolveThemeWithContrast, readableTextOn } from "@/lib/themes";

const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-", codeBlockStyle: "fenced" });
function htmlToMarkdown(html: string): string {
  return turndown.turndown(html || "").trim();
}

function NavGroup({ label, icon: Icon, children }: { label: string; icon: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// WAI-ARIA tabs pattern (manual activation): the section rail is a vertical
// tablist. Arrow keys (Up/Down and Left/Right), Home and End move focus with
// a roving tabindex; Enter/Space activates the focused tab. Link items (whole
// separate pages, e.g. Tools/History) participate in the roving focus order
// but activate by navigation.
function navRailKeyDown(e: React.KeyboardEvent<HTMLElement>) {
  const key = e.key;
  if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
  const container = e.currentTarget;
  const tabs = Array.from(container.querySelectorAll<HTMLElement>('[role="tab"], [data-rail-item]'))
    .filter((el) => !el.hasAttribute("aria-disabled") && el.offsetParent !== null);
  if (tabs.length === 0) return;
  const current = tabs.indexOf(document.activeElement as HTMLElement);
  let next = current;
  if (key === "Home") next = 0;
  else if (key === "End") next = tabs.length - 1;
  else if (key === "ArrowDown" || key === "ArrowRight") next = current < 0 ? 0 : (current + 1) % tabs.length;
  else if (key === "ArrowUp" || key === "ArrowLeft") next = current < 0 ? 0 : (current - 1 + tabs.length) % tabs.length;
  e.preventDefault(); // no accidental page scroll from arrow keys
  tabs[next]?.focus();
}

function NavItem({ id, icon: Icon, label, activeSection, onClick, href, external }: { id: string; icon: LucideIcon; label: string; activeSection: string; onClick: (id: string) => void; href?: string; external?: boolean }) {
  const isActive = activeSection === id;
  const cls = `w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
    isActive
      ? "bg-primary/10 text-primary font-medium"
      : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;
  const tabProps = {
    role: "tab" as const,
    id: `tab-${id}`,
    "aria-selected": isActive,
    tabIndex: isActive ? 0 : -1,
    "data-testid": `tab-${id}`,
  };
  // Rail items that NAVIGATE (route/external links) are ordinary links, not
  // tabs: they don't control the section tabpanel, so role=tab/aria-selected
  // would be invalid ARIA. They stay arrow-key reachable via data-rail-item
  // (roving handled by navRailKeyDown) with tabIndex -1.
  const linkProps = {
    id: `tab-${id}`,
    tabIndex: -1,
    "data-rail-item": "true",
    "data-testid": `tab-${id}`,
  };
  if (href && external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...linkProps} className={cls}>
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
        <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
      </a>
    );
  }
  if (href) {
    return (
      <Link href={href} {...linkProps} className={cls}>
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </Link>
    );
  }
  return (
    <button
      type="button"
      {...tabProps}
      aria-controls="bot-section-panel"
      onClick={() => onClick(id)}
      className={cls}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{label}</span>
      {isActive && <ChevronRight className="w-3 h-3 ml-auto" aria-hidden="true" />}
    </button>
  );
}

interface ProviderModels {
  provider: string;
  providerName: string;
  models: string[];
}

const iconOptions = [
  { value: "bot", label: "Robot", icon: Bot },
  { value: "message", label: "Message", icon: MessageSquare },
  { value: "globe", label: "Globe", icon: Globe },
  { value: "zap", label: "Lightning", icon: Zap },
  { value: "file", label: "Document", icon: FileText },
  { value: "user", label: "User", icon: User },
];

function getBotTypeIcon(type: string) {
  switch (type) {
    case "website": return Globe;
    case "document": return FileText;
    case "combo": return Layers;
    case "ai-enhanced": return Zap;
    default: return Bot;
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <IconButton variant="ghost" label={copied ? "Copied to clipboard" : "Copy to clipboard"} onClick={handleCopy} data-testid="button-copy">
      {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
    </IconButton>
  );
}

function SourceContentViewer({ chatbotId, source, onClose }: { chatbotId: string; source: KnowledgeSource; onClose: () => void }) {
  const { data, isLoading } = useQuery<{
    source: { id: string; name: string; type: string; url?: string; status: string; contentLength: number; createdAt: string };
    chunks: { id: string; chunkIndex: number; content: string; contentLength: number }[];
    totalChunks: number;
    totalCharacters: number;
  }>({
    queryKey: ["/api/chatbots", chatbotId, "knowledge", source.id, "content"],
  });

  const [expandedChunks, setExpandedChunks] = useState<Set<number>>(new Set());

  const toggleChunk = (idx: number) => {
    setExpandedChunks((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const expandAll = () => {
    if (data) setExpandedChunks(new Set(data.chunks.map((_, i) => i)));
  };

  const collapseAll = () => setExpandedChunks(new Set());

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {source.type === "url" ? <Globe className="w-4 h-4 text-primary flex-shrink-0" /> : <FileText className="w-4 h-4 text-primary flex-shrink-0" />}
            <div className="min-w-0">
              <CardTitle className="text-sm truncate">{source.name}</CardTitle>
              {source.url && <CardDescription className="truncate">{source.url}</CardDescription>}
            </div>
          </div>
          <IconButton variant="ghost" label="Close extracted content" onClick={onClose} data-testid="button-close-content">
            <X className="w-4 h-4" aria-hidden="true" />
          </IconButton>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-md bg-muted/50 text-center">
                <p className="text-lg font-semibold" data-testid="text-total-chunks">{data.totalChunks}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Chunks</p>
              </div>
              <div className="p-3 rounded-md bg-muted/50 text-center">
                <p className="text-lg font-semibold" data-testid="text-total-chars">{(data.totalCharacters / 1000).toFixed(1)}k</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Characters</p>
              </div>
              <div className="p-3 rounded-md bg-muted/50 text-center">
                <p className="text-lg font-semibold">{data.source.status}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Status</p>
              </div>
            </div>

            {data.chunks.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-muted-foreground">Extracted Content ({data.totalChunks} chunks)</p>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={expandAll} data-testid="button-expand-all">Expand All</Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={collapseAll} data-testid="button-collapse-all">Collapse All</Button>
                  </div>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {data.chunks.map((chunk, idx) => (
                    <div key={chunk.id} className="border rounded-md overflow-hidden" data-testid={`chunk-${idx}`}>
                      <button
                        onClick={() => toggleChunk(idx)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-xs font-medium">Chunk {chunk.chunkIndex + 1}</span>
                        <span className="text-[10px] text-muted-foreground">{chunk.contentLength.toLocaleString()} chars</span>
                      </button>
                      {expandedChunks.has(idx) && (
                        <div className="px-3 pb-3 border-t">
                          <pre className="text-xs whitespace-pre-wrap break-words mt-2 text-muted-foreground leading-relaxed max-h-[300px] overflow-y-auto">
                            {chunk.content}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {data.chunks.length === 0 && (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">No content has been extracted yet. The source may still be processing.</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load content.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Evidence helpers for HIPAA Verification Details modal ─────────────────────

const HIPAA_EVIDENCE_TYPES: Record<string, string> = {
  policy_document:      "Policy Document",
  procedure:            "Procedure",
  screenshot:           "Screenshot",
  configuration_record: "Configuration Record",
  audit_report:         "Audit Report",
  external_reference:   "External Reference",
  other:                "Other",
};

function parseEvidenceJson(raw: string | null): null | Record<string, string> {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === "object" && typeof p.title === "string" && p.title) return p;
  } catch { /* fall through */ }
  return null;
}

function isUrl(s: string) {
  try { return /^https?:\/\//.test(s); } catch { return false; }
}

function evidenceFileIcon(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "png" || ext === "jpg" || ext === "jpeg") return "🖼";
  return "📎";
}

function evidenceFormatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Renders evidenceReference as a structured card (structured JSON) or plain text (legacy). */
function EvidenceDisplay({ raw }: { raw: string }) {
  const parsed = parseEvidenceJson(raw);

  if (!parsed) {
    // Legacy plain text — show as simple labelled note
    return (
      <div className="mt-2 rounded border border-muted bg-muted/30 px-2.5 py-2 text-xs">
        <p className="font-semibold text-foreground mb-0.5">Evidence</p>
        <p className="text-muted-foreground break-words">{raw}</p>
      </div>
    );
  }

  const typeLabel = HIPAA_EVIDENCE_TYPES[parsed.type] ?? parsed.type;
  const addedAt   = parsed.addedAt
    ? new Date(parsed.addedAt).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" })
    : null;

  // attachment may be a nested object inside the parsed record
  const attachment = parsed.attachment && typeof parsed.attachment === "object"
    ? parsed.attachment as { name: string; size: number; dataUrl: string }
    : null;

  return (
    <div className="mt-2 rounded border border-blue-200 bg-blue-50/40 px-3 py-2.5 text-xs space-y-2">
      <p className="font-semibold text-blue-900">Evidence</p>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
        {parsed.title && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Title:</span>
            <span className="text-foreground break-words">{parsed.title}</span>
          </>
        )}
        {parsed.type && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Evidence Type:</span>
            <span className="text-foreground break-words">{typeLabel}</span>
          </>
        )}
        {parsed.description && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Description:</span>
            <span className="text-foreground break-words">{parsed.description}</span>
          </>
        )}
        {parsed.reference && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Reference:</span>
            {isUrl(parsed.reference) ? (
              <a
                href={parsed.reference}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline break-all"
              >
                {parsed.reference}
              </a>
            ) : (
              <span className="text-foreground break-words">{parsed.reference}</span>
            )}
          </>
        )}
        {attachment && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap col-span-2 mt-1 border-t border-blue-100 pt-2">
              Attachment
            </span>
            <div className="col-span-2 rounded border border-blue-100 bg-white/60 px-2.5 py-2 flex flex-col gap-1.5">
              {/* Filename row */}
              <div className="flex items-center gap-1.5">
                <span className="text-base leading-none flex-shrink-0" aria-hidden="true">
                  {evidenceFileIcon(attachment.name)}
                </span>
                <span className="font-medium text-foreground truncate">{attachment.name}</span>
              </div>
              {/* Size row */}
              <div className="flex items-center gap-1 text-muted-foreground">
                <span className="font-bold text-foreground">Size:</span>
                <span>{evidenceFormatBytes(attachment.size)}</span>
              </div>
              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-0.5">
                <a
                  href={attachment.dataUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  View
                </a>
                <a
                  href={attachment.dataUrl}
                  download={attachment.name}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-50 px-2 py-0.5 text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <Download className="w-3 h-3" aria-hidden="true" />
                  Download
                </a>
              </div>
            </div>
          </>
        )}
        {parsed.addedBy && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Added By:</span>
            <span className="text-foreground break-words">{parsed.addedBy}</span>
          </>
        )}
        {addedAt && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Added Date:</span>
            <span className="text-foreground">{addedAt}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Proper module-level component for the HIPAA verification detail dialog.
 * Must NOT be defined inside a render function — doing so creates a new
 * component type on every parent render, causing React to unmount/remount
 * the component (and lose the useQuery cache) continuously.
 */
function HipaaVerificationDetailDialog({
  sourceId,
  onClose,
}: {
  sourceId: string;
  onClose: () => void;
}) {
  const { data: verification, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/organization/compliance/hipaa/source-verifications", sourceId],
    queryFn: async () => {
      const token = getAuthToken();
      const r = await fetch(`/api/organization/compliance/hipaa/source-verifications/${sourceId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      });
      // Always check r.ok — 4xx Problem Details bodies are valid JSON but are
      // NOT verification data. Parsing them as-is causes passedCount=undefined→0.
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || body.title || `HTTP ${r.status}`);
      }
      return r.json();
    },
    enabled: !!sourceId,
    staleTime: 5_000,
    refetchInterval: (q) =>
      (q.state.data as any)?.overallStatus === "pending_verification" ? 3_000 : false,
  });

  const reverifyM = useMutation({
    mutationFn: async () => {
      const token = getAuthToken();
      const r = await fetch(
        `/api/organization/compliance/hipaa/source-verifications/${sourceId}/reverify`,
        { method: "POST", headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["/api/organization/compliance/hipaa/source-verifications", sourceId],
      }),
  });

  const statusBadgeClass = (s: string) =>
    s === "blocked"
      ? "text-red-700 font-semibold"
      : s === "active"
      ? "text-green-700 font-semibold"
      : "text-amber-700 font-semibold";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>HIPAA Verification Details</DialogTitle>
          {verification && !isError && (
            <DialogDescription>
              <span className="font-medium">{verification.sourceName}</span>{" — "}
              <span className={statusBadgeClass(verification.overallStatus ?? "")}>
                {(verification.overallStatus ?? "unknown").replace(/_/g, " ")}
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : isError ? (
          <div className="rounded bg-destructive/10 border border-destructive/20 px-3 py-3 text-sm text-destructive space-y-1">
            <p className="font-medium">Could not load verification details.</p>
            <p className="text-xs text-muted-foreground">
              Ensure you have HIPAA compliance access (compliance.view permission) and that verification has run for this source.
            </p>
          </div>
        ) : !verification ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No verification record found for this source. HIPAA verification runs automatically for URL sources in HIPAA-enabled organisations.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Counts row */}
            <div className="flex gap-4 text-sm flex-wrap">
              {(verification.passedCount ?? 0) > 0 && (
                <span className="text-green-700 font-medium">{verification.passedCount} passed</span>
              )}
              {(verification.failedCount ?? 0) > 0 && (
                <span className="text-red-700 font-medium">{verification.failedCount} failed</span>
              )}
              {(verification.manualRequiredCount ?? 0) > 0 && (
                <span className="text-amber-700 font-medium">{verification.manualRequiredCount} need evidence</span>
              )}
              {(verification.passedCount ?? 0) === 0 &&
               (verification.failedCount ?? 0) === 0 &&
               (verification.manualRequiredCount ?? 0) === 0 && (
                <span className="text-muted-foreground text-xs">No check results recorded yet.</span>
              )}
            </div>

            {/* Blocked banner */}
            {verification.overallStatus === "blocked" && (
              <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <strong>Source is blocked from chatbot retrieval.</strong>{" "}
                {verification.verificationSummary ||
                  "One or more required HIPAA security checks failed. Remediate the issues below and click Re-verify."}
              </div>
            )}

            {/* Pending review banner */}
            {verification.overallStatus === "pending_review" && (
              <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <strong>Manual evidence required.</strong>{" "}
                {verification.verificationSummary ||
                  "Automatic checks passed or are not applicable. Provide evidence for the manual controls below."}
              </div>
            )}

            {/* Control results */}
            {(verification.controlResults ?? []).length > 0 ? (
              <div className="space-y-2">
                {/* Auto checks first, then manual */}
                {(verification.controlResults as any[])
                  .sort((a: any, b: any) => {
                    const aAuto = a.verificationMethod === "automatic" ? 0 : 1;
                    const bAuto = b.verificationMethod === "automatic" ? 0 : 1;
                    return aAuto - bAuto || (a.controlName ?? "").localeCompare(b.controlName ?? "");
                  })
                  .map((r: any) => {
                    const isPassed  = r.complianceStatus === "passed";
                    const isFailed  = r.complianceStatus === "failed";
                    const isPending = r.complianceStatus === "manual_required";
                    const isManual  = r.verificationMethod !== "automatic";

                    const cardClass = isFailed
                      ? "border-red-200 bg-red-50/40"
                      : isPending
                      ? "border-amber-200 bg-amber-50/40"
                      : isPassed
                      ? "border-green-100 bg-green-50/20"
                      : "border-muted bg-muted/10";

                    return (
                      <div key={r.id} className={`rounded-lg border p-3 text-xs ${cardClass}`}>
                        {/* Header: icon + name + badges */}
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            {isPassed  && <Check  className="w-3.5 h-3.5 text-green-600 flex-shrink-0" aria-hidden="true" />}
                            {isFailed  && <X      className="w-3.5 h-3.5 text-red-600 flex-shrink-0"   aria-hidden="true" />}
                            {isPending && <Clock  className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" aria-hidden="true" />}
                            <span className={`font-semibold break-words ${isFailed ? "text-red-800" : isPending ? "text-amber-800" : ""}`}>
                              {r.controlName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isPassed  && <Badge className="text-[10px] h-5 px-1.5 bg-green-600 hover:bg-green-600">PASSED</Badge>}
                            {isFailed  && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">FAILED</Badge>}
                            {isPending && <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">PENDING</Badge>}
                            {isManual  && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-300 text-blue-700">Manual</Badge>}
                          </div>
                        </div>

                        {/* Finding */}
                        {r.findings && (
                          <div className="mt-2 rounded bg-red-50 border border-red-200 px-2.5 py-1.5">
                            <p className="font-medium text-red-800 mb-0.5">Finding:</p>
                            <p className="text-red-700 break-words">{r.findings}</p>
                          </div>
                        )}

                        {/* Evidence required description */}
                        {r.evidenceRequired && !isPassed && (
                          <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5">
                            <span className="font-medium text-amber-800">Required: </span>
                            <span className="text-amber-700">{r.evidenceRequired}</span>
                          </div>
                        )}

                        {/* Evidence — structured card or legacy plain text */}
                        {r.evidenceReference && (
                          <EvidenceDisplay raw={r.evidenceReference} />
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              /* Blocked but no results — verification likely failed mid-run */
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <strong>Verification incomplete.</strong> The source status is recorded but individual check results were not
                persisted. Click Re-verify to re-run the full check and populate the results.
              </div>
            )}

            {verification.verificationCompletedAt && (
              <p className="text-xs text-muted-foreground">
                Verified {new Date(verification.verificationCompletedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {!isError && verification &&
            (verification.overallStatus === "blocked" || verification.overallStatus === "pending_review") && (
              <Button variant="outline" onClick={() => reverifyM.mutate()} disabled={reverifyM.isPending}>
                <RefreshCw
                  className={`w-4 h-4 mr-1 ${reverifyM.isPending ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                {reverifyM.isPending ? "Re-verifying…" : "Re-verify"}
              </Button>
            )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Module-level component for the FERPA verification detail dialog.
 * Mirrors HipaaVerificationDetailDialog — must NOT be defined inside a render
 * function to avoid React unmount/remount on every parent render.
 */
function FerpaVerificationDetailDialog({
  sourceId,
  onClose,
}: {
  sourceId: string;
  onClose: () => void;
}) {
  const { data: verification, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/organization/compliance/ferpa/source-verifications", sourceId],
    queryFn: async () => {
      const token = getAuthToken();
      const r = await fetch(`/api/organization/compliance/ferpa/source-verifications/${sourceId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || body.title || `HTTP ${r.status}`);
      }
      return r.json();
    },
    enabled: !!sourceId,
    staleTime: 5_000,
    refetchInterval: (q) =>
      (q.state.data as any)?.overallStatus === "pending_verification" ? 3_000 : false,
  });

  const reverifyM = useMutation({
    mutationFn: async () => {
      const token = getAuthToken();
      const r = await fetch(
        `/api/organization/compliance/ferpa/source-verifications/${sourceId}/reverify`,
        { method: "POST", headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["/api/organization/compliance/ferpa/source-verifications", sourceId],
      }),
  });

  const statusBadgeClass = (s: string) =>
    s === "blocked"
      ? "text-red-700 font-semibold"
      : s === "active"
      ? "text-green-700 font-semibold"
      : s === "risk_accepted"
      ? "text-amber-600 font-semibold"
      : "text-yellow-700 font-semibold";

  const statusLabel = (s: string) =>
    s === "active"               ? "Passed"          :
    s === "blocked"              ? "Blocked"          :
    s === "risk_accepted"        ? "Risk Accepted"    :
    s === "pending_review"       ? "Pending Review"   :
    s === "pending_verification" ? "Verifying…"       :
    s.replace(/_/g, " ");

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>FERPA Verification Details</DialogTitle>
          {verification && !isError && (
            <DialogDescription>
              <span className="font-medium">{verification.sourceName}</span>{" — "}
              <span className={statusBadgeClass(verification.overallStatus ?? "")}>
                {statusLabel(verification.overallStatus ?? "unknown")}
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : isError ? (
          <div className="rounded bg-destructive/10 border border-destructive/20 px-3 py-3 text-sm text-destructive space-y-1">
            <p className="font-medium">Could not load FERPA verification details.</p>
            <p className="text-xs text-muted-foreground">
              Ensure you have FERPA compliance access (compliance.view permission) and that verification has run for this source.
            </p>
          </div>
        ) : !verification ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No FERPA verification record found for this source. FERPA verification runs automatically for URL sources in FERPA-enabled organisations.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Counts row */}
            <div className="flex gap-4 text-sm flex-wrap">
              {(verification.passedCount ?? 0) > 0 && (
                <span className="text-green-700 font-medium">{verification.passedCount} passed</span>
              )}
              {(verification.failedCount ?? 0) > 0 && (
                <span className="text-red-700 font-medium">{verification.failedCount} failed</span>
              )}
              {(verification.manualRequiredCount ?? 0) > 0 && (
                <span className="text-amber-700 font-medium">{verification.manualRequiredCount} need evidence</span>
              )}
              {(verification.passedCount ?? 0) === 0 &&
               (verification.failedCount ?? 0) === 0 &&
               (verification.manualRequiredCount ?? 0) === 0 && (
                <span className="text-muted-foreground text-xs">No check results recorded yet.</span>
              )}
            </div>

            {/* Status banners */}
            {verification.overallStatus === "blocked" && (
              <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <strong>Source is blocked from chatbot retrieval.</strong>{" "}
                {verification.verificationSummary ||
                  "One or more required FERPA security checks failed. Remediate the issues below and click Re-verify."}
              </div>
            )}
            {verification.overallStatus === "risk_accepted" && (
              <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <strong>Risk accepted.</strong>{" "}
                {verification.verificationSummary ||
                  "Compliance risk has been accepted. The source remains accessible. Review and re-verify as needed."}
              </div>
            )}
            {verification.overallStatus === "pending_review" && (
              <div className="rounded bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
                <strong>Manual evidence required.</strong>{" "}
                {verification.verificationSummary ||
                  "Automatic checks passed or are not applicable. Provide evidence for the manual controls below."}
              </div>
            )}

            {/* Control results */}
            {(verification.controlResults ?? []).length > 0 ? (
              <div className="space-y-2">
                {(verification.controlResults as any[])
                  .sort((a: any, b: any) => {
                    const aAuto = a.verificationMethod === "automatic" ? 0 : 1;
                    const bAuto = b.verificationMethod === "automatic" ? 0 : 1;
                    return aAuto - bAuto || (a.controlName ?? "").localeCompare(b.controlName ?? "");
                  })
                  .map((r: any) => {
                    const isPassed  = r.complianceStatus === "passed";
                    const isFailed  = r.complianceStatus === "failed";
                    const isPending = r.complianceStatus === "manual_required";
                    const isManual  = r.verificationMethod !== "automatic";

                    const cardClass = isFailed
                      ? "border-red-200 bg-red-50/40"
                      : isPending
                      ? "border-amber-200 bg-amber-50/40"
                      : isPassed
                      ? "border-green-100 bg-green-50/20"
                      : "border-muted bg-muted/10";

                    return (
                      <div key={r.id} className={`rounded-lg border p-3 text-xs ${cardClass}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            {isPassed  && <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" aria-hidden="true" />}
                            {isFailed  && <X     className="w-3.5 h-3.5 text-red-600 flex-shrink-0"   aria-hidden="true" />}
                            {isPending && <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" aria-hidden="true" />}
                            <span className={`font-semibold break-words ${isFailed ? "text-red-800" : isPending ? "text-amber-800" : ""}`}>
                              {r.controlName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isPassed  && <Badge className="text-[10px] h-5 px-1.5 bg-green-600 hover:bg-green-600">PASSED</Badge>}
                            {isFailed  && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">FAILED</Badge>}
                            {isPending && <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">PENDING</Badge>}
                            {isManual  && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-300 text-blue-700">Manual</Badge>}
                          </div>
                        </div>
                        {r.findings && (
                          <div className="mt-2 rounded bg-red-50 border border-red-200 px-2.5 py-1.5">
                            <p className="font-medium text-red-800 mb-0.5">Finding:</p>
                            <p className="text-red-700 break-words">{r.findings}</p>
                          </div>
                        )}
                        {r.evidenceRequired && !isPassed && (
                          <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5">
                            <span className="font-medium text-amber-800">Required: </span>
                            <span className="text-amber-700">{r.evidenceRequired}</span>
                          </div>
                        )}
                        {r.evidenceReference && (
                          <EvidenceDisplay raw={r.evidenceReference} />
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <strong>Verification incomplete.</strong> The source status is recorded but individual check results were not
                persisted. Click Re-verify to re-run the full check and populate the results.
              </div>
            )}

            {verification.verificationCompletedAt && (
              <p className="text-xs text-muted-foreground">
                Verified {new Date(verification.verificationCompletedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {!isError && verification &&
            (verification.overallStatus === "blocked" || verification.overallStatus === "pending_review") && (
              <Button variant="outline" onClick={() => reverifyM.mutate()} disabled={reverifyM.isPending}>
                <RefreshCw
                  className={`w-4 h-4 mr-1 ${reverifyM.isPending ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                {reverifyM.isPending ? "Re-verifying…" : "Re-verify"}
              </Button>
            )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Knowledge-source access control ──────────────────────────────────────────

type KsAccessPolicy = {
  allowedRoles?: string[];
  allowedDepartments?: string[];
  allowedUserGroups?: string[];
};

/** Minimal chip-style tag input (enter / comma to add, backspace to remove). */
function TagInput({
  label,
  placeholder,
  tags,
  setTags,
  inputVal,
  setInputVal,
}: {
  label: string;
  placeholder: string;
  tags: string[];
  setTags: React.Dispatch<React.SetStateAction<string[]>>;
  inputVal: string;
  setInputVal: React.Dispatch<React.SetStateAction<string>>;
}) {
  function commit(raw: string) {
    const trimmed = raw.trim().replace(/,/g, "");
    if (trimmed && !tags.includes(trimmed)) setTags([...tags, trimmed]);
    setInputVal("");
  }
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5 min-h-[30px] rounded-md border bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-0.5 rounded bg-secondary text-secondary-foreground text-[11px] px-1.5 py-0.5"
          >
            {t}
            <button
              type="button"
              onClick={() => setTags(tags.filter((x) => x !== t))}
              className="ml-0.5 opacity-60 hover:opacity-100"
              aria-label={`Remove ${t}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[80px] bg-transparent text-[11px] outline-none placeholder:text-muted-foreground/50"
          placeholder={tags.length === 0 ? placeholder : "Add…"}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) {
              e.preventDefault();
              commit(inputVal);
            } else if (e.key === "Backspace" && !inputVal && tags.length > 0) {
              setTags(tags.slice(0, -1));
            }
          }}
          onBlur={() => { if (inputVal.trim()) commit(inputVal); }}
        />
      </div>
    </div>
  );
}

/**
 * Collapsible Access Control section rendered inside each knowledge-source card.
 * Lets admins restrict which roles / departments / user-groups can retrieve
 * chunks from this source during RAG. Saves via PATCH on the knowledge endpoint.
 */
function SourceAccessControlSection({
  source,
  chatbotId,
}: {
  source: KnowledgeSource;
  chatbotId: string;
}) {
  const { toast } = useToast();
  const existing: KsAccessPolicy | null = (source as any).accessPolicy ?? null;

  const [expanded, setExpanded] = useState(false);
  const [roles, setRoles] = useState<string[]>(existing?.allowedRoles ?? []);
  const [depts, setDepts] = useState<string[]>(existing?.allowedDepartments ?? []);
  const [groups, setGroups] = useState<string[]>(existing?.allowedUserGroups ?? []);
  const [roleInput, setRoleInput] = useState("");
  const [deptInput, setDeptInput] = useState("");
  const [groupInput, setGroupInput] = useState("");

  // Sync local state when the server data refreshes (e.g. after save).
  useEffect(() => {
    const p: KsAccessPolicy | null = (source as any).accessPolicy ?? null;
    setRoles(p?.allowedRoles ?? []);
    setDepts(p?.allowedDepartments ?? []);
    setGroups(p?.allowedUserGroups ?? []);
  }, [(source as any).accessPolicy]);

  const saveMutation = useMutation({
    mutationFn: async ({ clear }: { clear: boolean }) => {
      if (clear) {
        await apiRequest("PATCH", `/api/chatbots/${chatbotId}/knowledge/${source.id}`, {
          clearAccessPolicy: true,
        });
      } else {
        await apiRequest("PATCH", `/api/chatbots/${chatbotId}/knowledge/${source.id}`, {
          accessPolicy: { allowedRoles: roles, allowedDepartments: depts, allowedUserGroups: groups },
        });
      }
    },
    onSuccess: (_data, { clear }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "knowledge"] });
      setExpanded(false);
      toast({
        title: clear ? "Restrictions cleared" : "Access policy saved",
        description: clear
          ? "This source is now open to all users."
          : "RAG retrieval will honour the new policy.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const isRestricted =
    (existing?.allowedRoles?.length ?? 0) > 0 ||
    (existing?.allowedDepartments?.length ?? 0) > 0 ||
    (existing?.allowedUserGroups?.length ?? 0) > 0;

  const summary = isRestricted
    ? [
        ...(existing!.allowedRoles?.length ? [`Roles: ${existing!.allowedRoles.join(", ")}`] : []),
        ...(existing!.allowedDepartments?.length ? [`Depts: ${existing!.allowedDepartments.join(", ")}`] : []),
        ...(existing!.allowedUserGroups?.length ? [`Groups: ${existing!.allowedUserGroups.join(", ")}`] : []),
      ].join(" · ")
    : "Open to all users";

  return (
    <div className="px-4 pb-3 space-y-2">
      {/* ── Section header (always visible, clickable to expand) ──────── */}
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={`access-panel-${source.id}`}
      >
        <div className="flex items-center gap-1.5">
          {isRestricted ? (
            <ShieldCheck className="w-3 h-3 text-amber-500" aria-hidden="true" />
          ) : (
            <Shield className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
          )}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Access Control
          </p>
          {isRestricted && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-600 border-amber-300">
              Restricted
            </Badge>
          )}
        </div>
        <ChevronRight
          className={`w-3 h-3 text-muted-foreground transition-transform duration-150 ${expanded ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      </button>

      {/* ── Collapsed summary ─────────────────────────────────────────── */}
      {!expanded && (
        <p className="text-[11px] text-muted-foreground">{summary}</p>
      )}

      {/* ── Expanded editor ───────────────────────────────────────────── */}
      {expanded && (
        <div id={`access-panel-${source.id}`} className="space-y-3 pt-1">
          <TagInput
            label="Allowed Roles"
            placeholder="e.g. HR, Admin…"
            tags={roles}
            setTags={setRoles}
            inputVal={roleInput}
            setInputVal={setRoleInput}
          />
          <TagInput
            label="Allowed Departments"
            placeholder="e.g. HR, Finance…"
            tags={depts}
            setTags={setDepts}
            inputVal={deptInput}
            setInputVal={setDeptInput}
          />
          <TagInput
            label="Allowed User Groups"
            placeholder="e.g. managers…"
            tags={groups}
            setTags={setGroups}
            inputVal={groupInput}
            setInputVal={setGroupInput}
          />
          <p className="text-[10px] text-muted-foreground/70">
            Leave all fields empty to make this source open to everyone.
            Access is granted when a user matches <em>any</em> entry in <em>any</em> list.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => saveMutation.mutate({ clear: false })}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving…" : "Save Policy"}
            </Button>
            {isRestricted && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => saveMutation.mutate({ clear: true })}
                disabled={saveMutation.isPending}
              >
                Clear Restrictions
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                const p: KsAccessPolicy | null = (source as any).accessPolicy ?? null;
                setRoles(p?.allowedRoles ?? []);
                setDepts(p?.allowedDepartments ?? []);
                setGroups(p?.allowedUserGroups ?? []);
                setExpanded(false);
              }}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function KnowledgePanel({ chatbot }: { chatbot: Chatbot }) {
  const { toast } = useToast();
  const [newUrl, setNewUrl] = useState("");
  const [newUrlSchedule, setNewUrlSchedule] = useState("manual");
  const [textName, setTextName] = useState("");
  const [textHtml, setTextHtml] = useState("");
  const [textResetSignal, setTextResetSignal] = useState(0);
  const [viewingSource, setViewingSource] = useState<KnowledgeSource | null>(null);
  const [hipaaDetailSourceId, setHipaaDetailSourceId] = useState<string | null>(null);
  const [ferpaDetailSourceId, setFerpaDetailSourceId] = useState<string | null>(null);
  const [spSiteUrl, setSpSiteUrl] = useState("");
  const [spLibrary, setSpLibrary] = useState("");
  const [spFolder, setSpFolder] = useState("");
  const [spMock, setSpMock] = useState(false);
  const [lgMode, setLgMode] = useState<"url" | "api">("url");
  const [lgBaseUrl, setLgBaseUrl] = useState("");
  const [lgApiBaseUrl, setLgApiBaseUrl] = useState("https://lgapi-us.libapps.com/1.2");
  const [lgSiteId, setLgSiteId] = useState("");
  const [lgClientId, setLgClientId] = useState("");
  const [lgClientSecret, setLgClientSecret] = useState("");
  const [lgMock, setLgMock] = useState(false);
  const { data: sources, isLoading } = useQuery<KnowledgeSource[]>({
    queryKey: ["/api/chatbots", chatbot.id, "knowledge"],
    refetchInterval: (query) => {
      const rows = query.state.data as KnowledgeSource[] | undefined;
      return rows?.some(
        (s) => s.status === "pending" ||
               (s as any).hipaaVerificationStatus === "pending_verification" ||
               (s as any).ferpaVerificationStatus === "pending_verification"
      ) ? 2000 : false;
    },
  });

  const addUrlMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/chatbots/${chatbot.id}/knowledge`, {
        chatbotId: chatbot.id,
        type: "url",
        name: new URL(newUrl).hostname,
        url: newUrl,
        recrawlFrequency: newUrlSchedule,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      setNewUrl("");
      setNewUrlSchedule("manual");
      toast({ title: "URL added", description: "Website content will be processed." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const patchScheduleMutation = useMutation({
    mutationFn: async ({ id, recrawlFrequency }: { id: string; recrawlFrequency: string }) => {
      await apiRequest("PATCH", `/api/chatbots/${chatbot.id}/knowledge/${id}`, { recrawlFrequency });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
    },
    onError: (err: Error) => {
      toast({ title: "Schedule update failed", description: err.message, variant: "destructive" });
    },
  });

  const recrawlNowMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", sources?.find(s => s.id === id)?.type === "sharepoint_document"
        ? `/api/chatbots/${chatbot.id}/sharepoint-document/${id}/refresh`
        : `/api/chatbots/${chatbot.id}/knowledge/${id}/reprocess`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      toast({ title: "Re-crawl started", description: "Content will be updated shortly." });
    },
    onError: (err: Error) => {
      toast({ title: "Re-crawl failed", description: err.message, variant: "destructive" });
    },
  });

  const addTextMutation = useMutation({
    mutationFn: async () => {
      const markdown = htmlToMarkdown(textHtml);
      if (!markdown) throw new Error("Add some content before saving.");
      await apiRequest("POST", `/api/chatbots/${chatbot.id}/knowledge`, {
        chatbotId: chatbot.id,
        type: "text",
        name: textName.trim() || "Text note",
        content: markdown,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      setTextName("");
      setTextHtml("");
      setTextResetSignal((n) => n + 1);
      toast({ title: "Text added", description: "Content has been processed into the knowledge base." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const addConnectorMutation = useMutation({
    mutationFn: async (payload: { type: string; name: string; config: Record<string, string> }) => {
      await apiRequest("POST", `/api/chatbots/${chatbot.id}/knowledge`, {
        chatbotId: chatbot.id,
        type: payload.type,
        name: payload.name,
        config: payload.config,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      toast({ title: "Source added", description: "Content is being synced." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const addSharePoint = () => {
    if (spMock) {
      addConnectorMutation.mutate(
        { type: "sharepoint", name: "SharePoint · sample", config: { mock: "true" } },
        { onSuccess: () => { setSpMock(false); setSpSiteUrl(""); setSpLibrary(""); setSpFolder(""); } }
      );
      return;
    }
    const cfg: Record<string, string> = { siteUrl: spSiteUrl.trim() };
    if (spLibrary.trim()) cfg.library = spLibrary.trim();
    if (spFolder.trim()) cfg.folder = spFolder.trim();
    let name = "SharePoint";
    try { name = `SharePoint · ${new URL(spSiteUrl).pathname.replace(/^\/+|\/+$/g, "") || new URL(spSiteUrl).hostname}`; } catch {}
    addConnectorMutation.mutate(
      { type: "sharepoint", name, config: cfg },
      { onSuccess: () => { setSpSiteUrl(""); setSpLibrary(""); setSpFolder(""); } }
    );
  };

  const addLibGuides = () => {
    if (lgMock) {
      addConnectorMutation.mutate(
        { type: "libguides", name: "LibGuides · sample", config: { mock: "true" } },
        { onSuccess: () => { setLgMock(false); setLgBaseUrl(""); setLgSiteId(""); setLgClientId(""); setLgClientSecret(""); } }
      );
      return;
    }
    let cfg: Record<string, string>;
    let name = "LibGuides";
    if (lgMode === "api") {
      cfg = {
        apiBaseUrl: lgApiBaseUrl.trim(),
        siteId: lgSiteId.trim(),
        clientId: lgClientId.trim(),
        clientSecret: lgClientSecret.trim(),
      };
      name = `LibGuides · site ${lgSiteId.trim()}`;
    } else {
      cfg = { baseUrl: lgBaseUrl.trim() };
      try { name = `LibGuides · ${new URL(lgBaseUrl).hostname}`; } catch {}
    }
    addConnectorMutation.mutate(
      { type: "libguides", name, config: cfg },
      { onSuccess: () => { setLgBaseUrl(""); setLgSiteId(""); setLgClientId(""); setLgClientSecret(""); } }
    );
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const token = getAuthToken();
      const res = await fetch(`/api/chatbots/${chatbot.id}/knowledge/upload`, {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error(friendlyErrorMessage(res.status, await res.text().catch(() => "")));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      toast({ title: "Document uploaded", description: "Content will be processed." });
    },
    onError: (err: Error) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const restoreKnowledgeSource = async (id: string) => {
    try {
      await apiRequest("POST", `/api/chatbots/${chatbot.id}/knowledge/${id}/restore`);
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      toast({ title: "Source restored" });
    } catch (err: any) {
      toast({ title: "Restore failed", description: err?.message, variant: "destructive" });
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/chatbots/${chatbot.id}/knowledge/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
      toast({
        title: "Source archived",
        action: (
          <ToastAction altText="Undo — restore archived knowledge source" onClick={() => restoreKnowledgeSource(id)} data-testid="button-undo-archive-source">
            Undo
          </ToastAction>
        ),
      });
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", sources?.find(s => s.id === id)?.type === "sharepoint_document"
        ? `/api/chatbots/${chatbot.id}/sharepoint-document/${id}/refresh`
        : `/api/chatbots/${chatbot.id}/knowledge/${id}/reprocess`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "knowledge"] });
    },
    onError: (err: Error) => {
      toast({ title: "Retry failed", description: err.message, variant: "destructive" });
    },
  });

  const SUPPORTED_UPLOAD_EXTENSIONS = [".pdf", ".txt", ".md", ".csv", ".docx", ".xlsx", ".pptx"];
  const MAX_UPLOAD_BYTES = 250 * 1024 * 1024; // 250 MB

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected after an error.
    e.target.value = "";
    if (!file) return;

    const lastDot = file.name.lastIndexOf(".");
    const ext = lastDot >= 0 ? file.name.slice(lastDot).toLowerCase() : "";
    if (!SUPPORTED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast({
        title: "Unsupported file type",
        description: "Accepted formats: PDF (.pdf), Word (.docx), Excel (.xlsx), PowerPoint (.pptx), plain text (.txt, .md, .csv).",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: "File too large",
        description: "Maximum file size is 250 MB.",
        variant: "destructive",
      });
      return;
    }

    uploadMutation.mutate(file);
  };

  const showUrl = chatbot.type === "website" || chatbot.type === "combo" || chatbot.type === "ai-enhanced";
  const showUpload = chatbot.type === "document" || chatbot.type === "combo" || chatbot.type === "ai-enhanced";

  if (viewingSource) {
    return (
      <div className="space-y-4">
        <SourceContentViewer chatbotId={chatbot.id} source={viewingSource} onClose={() => setViewingSource(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SharePointDocumentSource botId={chatbot.id} />
      {showUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Add Website URL</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="https://example.com" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} className="pl-9" data-testid="input-add-url" />
              </div>
              <Button onClick={() => addUrlMutation.mutate()} disabled={!newUrl || addUrlMutation.isPending} data-testid="button-add-url">
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap text-muted-foreground">Re-crawl schedule</Label>
              <Select value={newUrlSchedule} onValueChange={setNewUrlSchedule}>
                <SelectTrigger className="h-8 text-xs w-36" data-testid="select-recrawl-schedule">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual only</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-muted/40">
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                <p>Crawls the URL and extracts text content. The full website is crawled — every level, with no depth limit.</p>
                <p className="mt-1">Limits: Up to <strong>2000 pages</strong> per URL. Content is chunked into segments for efficient retrieval.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {showUpload && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Upload Document</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-md cursor-pointer transition-colors border-muted-foreground/20" data-testid="label-upload-area">
              <Upload className="w-8 h-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">
                {uploadMutation.isPending ? "Uploading..." : "Click to upload a document"}
              </span>
              <input type="file" className="hidden" accept=".pdf,.docx,.xlsx,.pptx,.txt,.md,.csv" onChange={handleFileUpload} data-testid="input-file-upload" />
            </label>
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-muted/40">
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                <p><strong>Documents:</strong> .pdf, .docx</p>
                <p className="mt-0.5"><strong>Presentations:</strong> .pptx</p>
                <p className="mt-0.5"><strong>Spreadsheets:</strong> .xlsx</p>
                <p className="mt-0.5"><strong>Plain text:</strong> .txt, .md, .csv</p>
                <p className="mt-1">Max file size: <strong>250 MB</strong>. Files are automatically processed and split into chunks for training.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Type className="w-4 h-4" />Add Text</CardTitle>
          <CardDescription>Write or paste rich-formatted notes. Formatting is kept for readability — the bot reads the underlying text.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="input-text-name">Title</Label>
            <Input id="input-text-name" placeholder="e.g. Office hours & contact info" value={textName} onChange={(e) => setTextName(e.target.value)} data-testid="input-text-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Content</Label>
            <RichTextEditor
              placeholder="Type your content here. Use the toolbar to add headings, lists, bold, links…"
              resetSignal={textResetSignal}
              onChange={setTextHtml}
            />
          </div>
          <Button onClick={() => addTextMutation.mutate()} disabled={!htmlToMarkdown(textHtml) || addTextMutation.isPending} data-testid="button-add-text">
            <Plus className="w-4 h-4 mr-1" />{addTextMutation.isPending ? "Adding…" : "Add Text"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" />Add SharePoint Site</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between gap-2 p-2.5 rounded-md border border-dashed">
            <div className="space-y-0.5">
              <Label className="text-xs flex items-center gap-1.5" htmlFor="switch-sharepoint-mock"><FlaskConical className="w-3.5 h-3.5" /> Demo / sample mode</Label>
              <p className="text-[11px] text-muted-foreground">Load realistic sample SharePoint content — no Microsoft 365 connection needed.</p>
            </div>
            <Switch id="switch-sharepoint-mock" checked={spMock} onCheckedChange={setSpMock} data-testid="switch-sharepoint-mock" />
          </div>
          {!spMock && (
          <>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="input-sharepoint-url">Site URL</Label>
            <Input id="input-sharepoint-url" placeholder="https://contoso.sharepoint.com/sites/library" value={spSiteUrl} onChange={(e) => setSpSiteUrl(e.target.value)} data-testid="input-sharepoint-url" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="input-sharepoint-library">Document library <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="input-sharepoint-library" placeholder="Documents" value={spLibrary} onChange={(e) => setSpLibrary(e.target.value)} data-testid="input-sharepoint-library" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="input-sharepoint-folder">Folder <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="input-sharepoint-folder" placeholder="Policies/2026" value={spFolder} onChange={(e) => setSpFolder(e.target.value)} data-testid="input-sharepoint-folder" />
            </div>
          </div>
          </>
          )}
          <Button onClick={addSharePoint} disabled={(!spMock && !spSiteUrl) || addConnectorMutation.isPending} data-testid="button-add-sharepoint">
            <Plus className="w-4 h-4 mr-1" />{spMock ? "Add sample SharePoint" : "Add SharePoint"}
          </Button>
          {!spMock && (
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-muted/40">
            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-muted-foreground leading-relaxed">
              <p>Reuses this chatbot's <strong>Microsoft 365</strong> integration credentials (connect it under Integrations first). Reads documents and site pages — nothing is written back.</p>
              <p className="mt-1">Use <strong>Sites.Selected</strong> application permission with admin consent and an explicit Read grant for the selected site.</p>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Library className="w-4 h-4" />Add LibGuides</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between gap-2 p-2.5 rounded-md border border-dashed">
            <div className="space-y-0.5">
              <Label className="text-xs flex items-center gap-1.5" htmlFor="switch-libguides-mock"><FlaskConical className="w-3.5 h-3.5" /> Demo / sample mode</Label>
              <p className="text-[11px] text-muted-foreground">Load realistic sample LibGuides content — no site URL or API credentials needed.</p>
            </div>
            <Switch id="switch-libguides-mock" checked={lgMock} onCheckedChange={setLgMock} data-testid="switch-libguides-mock" />
          </div>
          {!lgMock && (
          <>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant={lgMode === "url" ? "default" : "outline"} onClick={() => setLgMode("url")} data-testid="button-libguides-mode-url">Published URL</Button>
            <Button type="button" size="sm" variant={lgMode === "api" ? "default" : "outline"} onClick={() => setLgMode("api")} data-testid="button-libguides-mode-api">API credentials</Button>
          </div>
          {lgMode === "url" ? (
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="input-libguides-url">Guides base URL</Label>
              <Input id="input-libguides-url" placeholder="https://libguides.example.edu" value={lgBaseUrl} onChange={(e) => setLgBaseUrl(e.target.value)} data-testid="input-libguides-url" />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="space-y-1.5">
                <Label className="text-xs" htmlFor="input-libguides-apibase">API base URL</Label>
                <Input id="input-libguides-apibase" placeholder="https://lgapi-us.libapps.com/1.2" value={lgApiBaseUrl} onChange={(e) => setLgApiBaseUrl(e.target.value)} data-testid="input-libguides-apibase" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="input-libguides-siteid">Site ID</Label>
                  <Input id="input-libguides-siteid" placeholder="1234" value={lgSiteId} onChange={(e) => setLgSiteId(e.target.value)} data-testid="input-libguides-siteid" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="input-libguides-clientid">Client ID</Label>
                  <Input id="input-libguides-clientid" value={lgClientId} onChange={(e) => setLgClientId(e.target.value)} data-testid="input-libguides-clientid" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="input-libguides-clientsecret">Client secret</Label>
                  <Input id="input-libguides-clientsecret" type="password" value={lgClientSecret} onChange={(e) => setLgClientSecret(e.target.value)} data-testid="input-libguides-clientsecret" />
                </div>
              </div>
            </div>
          )}
          </>
          )}
          <Button
            onClick={addLibGuides}
            disabled={addConnectorMutation.isPending || (!lgMock && (lgMode === "url" ? !lgBaseUrl : (!lgApiBaseUrl || !lgSiteId || !lgClientId || !lgClientSecret)))}
            data-testid="button-add-libguides"
          >
            <Plus className="w-4 h-4 mr-1" />{lgMock ? "Add sample LibGuides" : "Add LibGuides"}
          </Button>
          {!lgMock && (
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-muted/40">
            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-muted-foreground leading-relaxed">
              <p><strong>Published URL:</strong> crawls your public LibGuides site (no credentials needed).</p>
              <p className="mt-1"><strong>API credentials:</strong> pulls published guides via the Springshare LibGuides API (OAuth client credentials). Credentials are stored securely and never shown again.</p>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Knowledge Sources ({sources?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sources && sources.length > 0 ? (
            <div className="space-y-2">
              {sources.map((source) => {
                /* ── helpers ──────────────────────────────────────────── */
                const displayUrl =
                  source.url ??
                  (source as any).config?.siteUrl ??
                  (source as any).config?.baseUrl ??
                  ((source as any).config?.apiBaseUrl
                    ? `${(source as any).config.apiBaseUrl} · site ${(source as any).config.siteId}`
                    : null);

                const SourceIcon =
                  source.type === "url"        ? Globe      :
                  source.type === "sharepoint" ? Building2  :
                  source.type === "libguides"  ? Library    : FileText;

                const fs = (source as any).ferpaVerificationStatus as string | undefined;
                const hs = (source as any).hipaaVerificationStatus as string | undefined;

                /* FERPA badge config */
                const ferpaStatusLabel =
                  fs === "active"               ? "Passed"         :
                  fs === "blocked"              ? "Blocked"        :
                  fs === "allowed"              ? "Allowed"        :
                  fs === "risk_accepted"        ? "Risk Accepted"  :
                  fs === "pending_review"       ? "Pending Review" :
                  fs === "pending_verification" ? "Verifying…"     : fs ?? "";
                const ferpaStatusIcon =
                  fs === "active"  ? "✓" :
                  fs === "blocked" ? "✕" :
                  fs === "allowed" ? "✓" :
                  fs === "pending_verification" ? "…" : "!";
                const ferpaColorClass =
                  fs === "active"  || fs === "allowed"       ? "text-green-700 dark:text-green-400"  :
                  fs === "blocked"                            ? "text-destructive"                     :
                  fs === "risk_accepted"                      ? "text-amber-600 dark:text-amber-400"  :
                  fs === "pending_review"                     ? "text-yellow-700 dark:text-yellow-400":
                                                                "text-muted-foreground";
                const ferpaBorderClass =
                  fs === "active"  || fs === "allowed"       ? "border-green-200 dark:border-green-800"   :
                  fs === "blocked"                            ? "border-destructive/40"                     :
                  fs === "risk_accepted"                      ? "border-amber-200 dark:border-amber-800"   :
                  fs === "pending_review"                     ? "border-yellow-200 dark:border-yellow-800" :
                                                                "border-border";
                const ferpaTitle =
                  fs === "active"               ? "All automatic FERPA checks passed." :
                  fs === "blocked"              ? "One or more required FERPA checks failed. Source is blocked until remediated." :
                  fs === "allowed"              ? "FERPA compliance risk has been approved for this source." :
                  fs === "risk_accepted"        ? "FERPA compliance risk has been accepted for this source." :
                  fs === "pending_review"       ? "FERPA verification requires admin evidence. Source is paused until evidence is supplied." :
                  fs === "pending_verification" ? "FERPA verification is running…" : "";

                /* HIPAA badge config */
                const hipaaStatusLabel =
                  hs === "active"               ? "Passed"         :
                  hs === "blocked"              ? "Blocked"        :
                  hs === "pending_review"       ? "Pending Review" :
                  hs === "pending_verification" ? "Verifying…"     : hs ?? "";
                const hipaaStatusIcon =
                  hs === "active"               ? "✓" :
                  hs === "blocked"              ? "✕" :
                  hs === "pending_verification" ? "…" : "!";
                const hipaaColorClass =
                  hs === "active"               ? "text-green-700 dark:text-green-400"   :
                  hs === "blocked"              ? "text-destructive"                      :
                  hs === "pending_review"       ? "text-yellow-700 dark:text-yellow-400" :
                                                  "text-muted-foreground";
                const hipaaBorderClass =
                  hs === "active"               ? "border-green-200 dark:border-green-800"   :
                  hs === "blocked"              ? "border-destructive/40"                     :
                  hs === "pending_review"       ? "border-yellow-200 dark:border-yellow-800" :
                                                  "border-border";
                const hipaaTitle =
                  hs === "active"               ? "All automatic HIPAA checks passed." :
                  hs === "blocked"              ? "One or more required HIPAA checks failed. Source is blocked until remediated." :
                  hs === "pending_review"       ? "HIPAA verification requires admin evidence. Source is paused until evidence is supplied." :
                  hs === "pending_verification" ? "HIPAA verification is running…" : "";

                const hasCompliance = !!fs || !!hs;
                const canRecrawl = source.type === "url" && (source.status === "processed" || source.status === "error");
                const canRetry   = source.type !== "url" && source.status === "error";

                return (
                  <div
                    key={source.id}
                    className="rounded-lg border bg-card shadow-sm overflow-hidden"
                    data-testid={`source-${source.id}`}
                  >
                    {/* ── Source header ──────────────────────────────── */}
                    <div className="p-4 flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0 rounded-md bg-muted p-1.5">
                        <SourceIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold leading-snug break-words">{source.name}</p>
                        {displayUrl && (
                          <p className="text-xs text-muted-foreground mt-0.5 break-all leading-relaxed">{displayUrl}</p>
                        )}
                      </div>
                      {typeof (source as any).itemCount === "number" && (source as any).itemCount > 0 && (
                        <Badge variant="outline" className="flex-shrink-0 self-start mt-0.5 text-[11px]" data-testid={`badge-itemcount-${source.id}`}>
                          {(source as any).itemCount} items
                        </Badge>
                      )}
                    </div>

                    {/* ── Compliance status ───────────────────────────── */}
                    {hasCompliance && (
                      <div className="px-4 pb-3 space-y-2">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Compliance Status
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {/* FERPA badge */}
                          {fs && (
                            <button
                              type="button"
                              className={`inline-flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ferpaBorderClass}`}
                              title={ferpaTitle}
                              data-testid={`badge-ferpa-${source.id}`}
                              onClick={() => setFerpaDetailSourceId(source.id)}
                              onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && setFerpaDetailSourceId(source.id)}
                            >
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground leading-none">FERPA</span>
                              <span className={`text-xs font-semibold leading-none mt-1 ${ferpaColorClass}`}>
                                {ferpaStatusIcon} {ferpaStatusLabel}
                              </span>
                            </button>
                          )}
                          {/* HIPAA badge */}
                          {hs && (
                            <button
                              type="button"
                              className={`inline-flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${hipaaBorderClass}`}
                              title={hipaaTitle}
                              data-testid={`badge-hipaa-${source.id}`}
                              onClick={() => setHipaaDetailSourceId(source.id)}
                              onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && setHipaaDetailSourceId(source.id)}
                            >
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground leading-none">HIPAA</span>
                              <span className={`text-xs font-semibold leading-none mt-1 ${hipaaColorClass}`}>
                                {hipaaStatusIcon} {hipaaStatusLabel}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Processing status ───────────────────────────── */}
                    <div className="px-4 pb-3 space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Processing Status
                      </p>
                      <div className="space-y-1.5">
                        <div>
                          <Badge
                            variant={source.status === "processed" ? "default" : source.status === "error" ? "destructive" : "secondary"}
                            className="text-[11px]"
                          >
                            {source.status === "processed" ? "Processed" :
                             source.status === "pending"   ? "Processing…" :
                             source.status === "error"     ? "Error" : source.status}
                          </Badge>
                        </div>
                        {source.status === "pending" && (
                          <div className="space-y-1" data-testid={`progress-source-${source.id}`}>
                            <Progress value={66} className="h-1.5 animate-pulse" aria-label="Processing knowledge source" />
                            <p className="text-[11px] text-muted-foreground">Extracting content…</p>
                          </div>
                        )}
                        {source.status === "error" && (
                          <p className="text-[11px] text-destructive" data-testid={`error-source-${source.id}`}>
                            {(source as any).lastError || "Processing failed. Use Re-process to try again."}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ── Governance Status ────────────────────────────── */}
                    {(source as any).governanceStatus && (
                      <div className="px-4 pb-3 space-y-2" data-testid={`governance-section-${source.id}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Governance Status
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {(() => {
                            const gs = (source as any).governanceStatus as string;
                            const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
                              published: "default",
                              approved:  "outline",
                              submitted: "secondary",
                              rejected:  "destructive",
                            };
                            const labelMap: Record<string, string> = {
                              published: "✓ Published",
                              approved:  "✓ Approved — pending publish",
                              submitted: "⏳ Pending Approval",
                              rejected:  "✗ Rejected",
                              draft:     "Draft",
                              archived:  "Archived",
                            };
                            const extraClass =
                              gs === "submitted" ? "border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30" :
                              gs === "approved"  ? "border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-950/30" : "";
                            return (
                              <Badge
                                variant={variantMap[gs] ?? "secondary"}
                                className={`text-[11px] ${extraClass}`}
                                data-testid={`badge-governance-${source.id}`}
                              >
                                {labelMap[gs] ?? gs}
                              </Badge>
                            );
                          })()}
                          {(source as any).governanceStatus === "submitted" && (
                            <span className="text-[11px] text-muted-foreground">
                              Awaiting review — visible to OrgAdmins in the Approvals menu.
                            </span>
                          )}
                          {(source as any).governanceStatus === "published" && (source as any).publishedAt && (
                            <span className="text-[11px] text-muted-foreground">
                              Published {new Date((source as any).publishedAt).toLocaleDateString()}
                            </span>
                          )}
                          {(source as any).governanceStatus === "rejected" &&
                           (source as any).reviewComments && (
                            <span className="text-[11px] text-destructive/80">
                              {(source as any).reviewComments}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Crawl schedule (URL sources) ────────────────── */}
                    {source.type === "url" && (
                      <div className="px-4 pb-3 space-y-2" data-testid={`schedule-row-${source.id}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Crawl Schedule
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                          <Select
                            value={(source as any).recrawlFrequency ?? "manual"}
                            onValueChange={(val) => patchScheduleMutation.mutate({ id: source.id, recrawlFrequency: val })}
                          >
                            <SelectTrigger className="h-6 text-[11px] w-28 px-2 py-0" data-testid={`select-schedule-${source.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">Manual only</SelectItem>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                          {(source as any).lastSyncedAt && (
                            <p className="text-[11px] text-muted-foreground" data-testid={`lastsynced-url-${source.id}`}>
                              Last crawled {new Date((source as any).lastSyncedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {(source.type === "sharepoint" || source.type === "libguides") && (source as any).lastSyncedAt && (
                      <div className="px-4 pb-3" data-testid={`lastsynced-source-${source.id}`}>
                        <p className="text-[11px] text-muted-foreground">
                          Last synced {new Date((source as any).lastSyncedAt).toLocaleString()} · auto-refreshes daily
                        </p>
                      </div>
                    )}

                    {/* ── Access Control ──────────────────────────────── */}
                    <SourceAccessControlSection source={source} chatbotId={chatbot.id} />

                    {/* ── Actions ────────────────────────────────────── */}
                    <div className="border-t bg-muted/30 px-4 py-2.5 flex items-center gap-2 flex-wrap">
                      {source.status === "processed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1.5"
                          onClick={() => setViewingSource(source)}
                          data-testid={`button-view-source-${source.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                          View Details
                        </Button>
                      )}
                      {canRecrawl && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1.5"
                          onClick={() => recrawlNowMutation.mutate(source.id)}
                          disabled={recrawlNowMutation.isPending && recrawlNowMutation.variables === source.id}
                          data-testid={`button-recrawl-${source.id}`}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${recrawlNowMutation.isPending && recrawlNowMutation.variables === source.id ? "animate-spin" : ""}`} aria-hidden="true" />
                          Re-crawl
                        </Button>
                      )}
                      {canRetry && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1.5"
                          onClick={() => retryMutation.mutate(source.id)}
                          disabled={retryMutation.isPending}
                          data-testid={`button-retry-source-${source.id}`}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${retryMutation.isPending && retryMutation.variables === source.id ? "animate-spin" : ""}`} aria-hidden="true" />
                          Re-process
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                        onClick={() => deleteMutation.mutate(source.id)}
                        data-testid={`button-delete-source-${source.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No knowledge sources added yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── HIPAA Verification Detail Dialog ─────────────────────────────── */}
      {/* Rendered as a stable module-level component — NOT inside a closure/IIFE */}
      {hipaaDetailSourceId && (
        <HipaaVerificationDetailDialog
          sourceId={hipaaDetailSourceId}
          onClose={() => setHipaaDetailSourceId(null)}
        />
      )}

      {/* ── FERPA Verification Detail Dialog ─────────────────────────────── */}
      {ferpaDetailSourceId && (
        <FerpaVerificationDetailDialog
          sourceId={ferpaDetailSourceId}
          onClose={() => setFerpaDetailSourceId(null)}
        />
      )}
    </div>
  );
}

function RatingsPanel({ chatbotId }: { chatbotId: string }) {
  const { data } = useQuery<{ ratings: any[]; averageRating: number; totalRatings: number }>({
    queryKey: ["/api/chatbots", chatbotId, "ratings"],
  });

  if (!data) {
    return <Skeleton className="h-48 w-full" />;
  }

  const starCounts = [0, 0, 0, 0, 0];
  data.ratings.forEach((r) => {
    if (r.rating >= 1 && r.rating <= 5) starCounts[r.rating - 1]++;
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Star className="w-5 h-5" />Ratings Overview</CardTitle>
        </CardHeader>
        <CardContent>
          {data.totalRatings === 0 ? (
            <div className="text-center py-8">
              <Star className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No ratings yet. Ratings will appear here once users rate their chat experience.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold" data-testid="text-avg-rating">{data.averageRating}</div>
                <div className="flex justify-center gap-1 my-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className={`w-5 h-5 ${s <= Math.round(data.averageRating) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/30"}`} />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">{data.totalRatings} total rating{data.totalRatings !== 1 ? "s" : ""}</p>
              </div>
              <div className="md:col-span-2 space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = starCounts[star - 1];
                  const pct = data.totalRatings > 0 ? (count / data.totalRatings) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs w-8 text-right">{star} star</span>
                      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                        <div className="bg-yellow-500 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {data.ratings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.ratings.slice(0, 20).map((r: any) => (
                <div key={r.id} className="p-3 rounded-md bg-muted/50" data-testid={`rating-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/30"}`} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                  {r.feedback && <p className="text-sm text-muted-foreground">{r.feedback}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ReportData {
  totalConversations: number;
  totalMessages: number;
  totalUserMessages: number;
  totalBotMessages: number;
  averageRating: number;
  totalRatings: number;
  ratingDistribution: number[];
  averageResponseTime: number;
  messagesPerDay: Record<string, number>;
  conversationsPerDay: Record<string, number>;
  topQuestions: { question: string; count: number }[];
  recentAuditLogs: any[];
}

function ReportsPanel({ chatbot }: { chatbot: Chatbot }) {
  const { toast } = useToast();
  const { data: report, isLoading } = useQuery<ReportData>({
    queryKey: ["/api/chatbots", chatbot.id, "reports"],
  });

  const handleExport = async (format: "csv" | "json") => {
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/chatbots/${chatbot.id}/reports/export?format=${format}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        const detail = (await res.text().catch(() => "")) || "";
        throw new Error(
          res.status === 401 || res.status === 403
            ? "You don't have permission to export this report."
            : detail || `Export failed (${res.status})`
        );
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chatbot-report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Export downloaded", description: `Report exported as ${format.toUpperCase()}` });
    } catch (err: any) {
      toast({ title: "Export failed", description: err.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!report) {
    return <p className="text-sm text-muted-foreground">Unable to load reports.</p>;
  }

  const messageDays = Object.entries(report.messagesPerDay).sort(([a], [b]) => a.localeCompare(b));
  const convoDays = Object.entries(report.conversationsPerDay).sort(([a], [b]) => a.localeCompare(b));
  const maxMsgCount = Math.max(...messageDays.map(([, v]) => v), 1);
  const maxConvoCount = Math.max(...convoDays.map(([, v]) => v), 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />Reports & Analytics
        </h2>
        <div className="report-export-actions">
          <Button variant="outline" onClick={() => handleExport("csv")} data-testid="button-export-csv">
            <Download className="w-4 h-4 mr-1" />Export CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport("json")} data-testid="button-export-json">
            <Download className="w-4 h-4 mr-1" />Export JSON
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Conversations</span>
            </div>
            <p className="text-2xl font-bold" data-testid="text-report-conversations">{report.totalConversations}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total Messages</span>
            </div>
            <p className="text-2xl font-bold" data-testid="text-report-messages">{report.totalMessages}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Avg Rating</span>
            </div>
            <p className="text-2xl font-bold" data-testid="text-report-avg-rating">
              {report.totalRatings > 0 ? report.averageRating : "N/A"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Avg Response</span>
            </div>
            <p className="text-2xl font-bold" data-testid="text-report-response-time">
              {report.averageResponseTime > 0 ? `${report.averageResponseTime}s` : "N/A"}
            </p>
          </CardContent>
        </Card>
      </div>

      {messageDays.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Messages Per Day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {messageDays.slice(-30).map(([day, count]) => (
                <div key={day} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div
                    className="w-full bg-primary/80 rounded-t-sm min-h-[2px]"
                    style={{ height: `${(count / maxMsgCount) * 100}%` }}
                    title={`${day}: ${count} messages`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">{messageDays.slice(-30)[0]?.[0] || ""}</span>
              <span className="text-[10px] text-muted-foreground">{messageDays[messageDays.length - 1]?.[0] || ""}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {convoDays.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Conversations Per Day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {convoDays.slice(-30).map(([day, count]) => (
                <div key={day} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div
                    className="w-full bg-primary/60 rounded-t-sm min-h-[2px]"
                    style={{ height: `${(count / maxConvoCount) * 100}%` }}
                    title={`${day}: ${count} conversations`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">{convoDays.slice(-30)[0]?.[0] || ""}</span>
              <span className="text-[10px] text-muted-foreground">{convoDays[convoDays.length - 1]?.[0] || ""}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {report.topQuestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Top Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {report.topQuestions.map((q, i) => (
                <div key={i} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50" data-testid={`top-question-${i}`}>
                  <span className="text-sm truncate flex-1">{q.question}</span>
                  <Badge variant="secondary">{q.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {report.totalRatings > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = report.ratingDistribution[star - 1];
                const pct = report.totalRatings > 0 ? (count / report.totalRatings) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-xs w-8 text-right">{star} star</span>
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div className="bg-yellow-500 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {report.recentAuditLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recent Audit Trail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {report.recentAuditLogs.map((log: any) => (
                <div key={log.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50 text-xs" data-testid={`audit-log-${log.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">{log.action}</Badge>
                    <span className="truncate text-muted-foreground">{log.resource}{log.details ? ` - ${log.details}` : ""}</span>
                  </div>
                  <span className="text-muted-foreground flex-shrink-0">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const CHANNEL_OPTIONS = [
  { id: "web", label: "Web Widget", icon: Monitor, description: "Browser-based chat widget embedded on your website" },
  { id: "sms", label: "SMS", icon: Smartphone, description: "Receive and respond to text messages via Twilio" },
  { id: "email", label: "Email", icon: Mail, description: "Process inbound emails via SendGrid or Mailgun" },
  { id: "mobile", label: "Mobile App", icon: Smartphone, description: "Native mobile app integration via API" },
  { id: "api", label: "Direct API", icon: Code2, description: "Programmatic access via REST API" },
];

// Slack/Teams install cards. These channels are not toggled like web/sms but
// installed via OAuth into a specific workspace/tenant. We show install
// status, the workspace label, and Install / Reinstall / Disconnect actions
// gated to admins (the API will 403 non-admins anyway, but we hide the UI to
// avoid confusion).
type ChannelInstallation = {
  id: string;
  type: "slack" | "teams";
  externalTeamId: string;
  externalTeamName: string | null;
  botUserId: string | null;
  installedByUserId: string | null;
  status: string | null;
  createdAt: string | null;
};

function ChannelInstallCard({
  chatbotId,
  type,
  install,
  isAdmin,
}: {
  chatbotId: string;
  type: "slack" | "teams";
  install: ChannelInstallation | undefined;
  isAdmin: boolean;
}) {
  const { toast } = useToast();
  const brandShort = useBranding().shortName;
  const isSlack = type === "slack";
  // The OAuth callback redirects back here with ?channel=slack&error=...
  // when the workspace/tenant is already linked to a different bot. Read it
  // straight off window.location so we don't need to thread state through
  // the parent.
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const conflictForThisCard =
    params?.get("channel") === type &&
    (params?.get("error") === "workspace_conflict" || params?.get("error") === "tenant_conflict");
  const conflictOther = params?.get("other") || "another chatbot";
  const conflictWorkspace = params?.get("workspace") || params?.get("tenant") || "";
  const Icon = isSlack ? Slack : MessageCircle;
  const label = isSlack ? "Slack" : "Microsoft Teams";
  const description = isSlack
    ? `Install the ${brandShort} app in your Slack workspace. Staff can DM the assistant or @mention it in channels.`
    : `Install the ${brandShort} app in your Microsoft Teams tenant. Staff can chat 1:1 or @mention the assistant in a team channel.`;
  const installUrl = `/api/channels/${type}/install/start/${chatbotId}`;
  const installed = !!install && install.status === "active";

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!install) return;
      await apiRequest("DELETE", `/api/channel-installations/${install.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "channel-installations"] });
      toast({ title: `${label} disconnected` });
    },
    onError: (err: Error) => toast({ title: "Disconnect failed", description: err.message, variant: "destructive" }),
  });

  return (
    <Card data-testid={`card-channel-${type}`}>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {label}
          {installed ? (
            <Badge variant="default" className="ml-auto" data-testid={`badge-${type}-installed`}>Connected</Badge>
          ) : (
            <Badge variant="outline" className="ml-auto" data-testid={`badge-${type}-not-installed`}>Not connected</Badge>
          )}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {conflictForThisCard && (
          <div
            className="text-xs rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2"
            data-testid={`alert-${type}-conflict`}
          >
            {isSlack ? "This Slack workspace" : "This Microsoft Teams tenant"}
            {conflictWorkspace ? ` (${conflictWorkspace})` : ""} is already connected to <strong>{conflictOther}</strong>.
            Disconnect it from that chatbot first, then try installing again.
          </div>
        )}
        {installed && install ? (
          <div className="text-xs text-muted-foreground space-y-1">
            <div data-testid={`text-${type}-workspace`}>
              <span className="font-medium text-foreground">{isSlack ? "Workspace" : "Tenant"}:</span>{" "}
              {install.externalTeamName || install.externalTeamId}
            </div>
            {install.createdAt && (
              <div>
                <span className="font-medium text-foreground">Installed:</span>{" "}
                {new Date(install.createdAt).toLocaleString()}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {isAdmin ? "Click Install to start the OAuth consent flow." : "Ask an admin to install this channel."}
          </p>
        )}
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" data-testid={`button-${type}-install`}>
              <a href={installUrl}>
                <Plug className="w-4 h-4 mr-1.5" />{installed ? "Reinstall" : "Install"}
              </a>
            </Button>
            {installed && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
                data-testid={`button-${type}-disconnect`}
              >
                <X className="w-4 h-4 mr-1.5" />Disconnect
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChannelsPanel({ chatbot }: { chatbot: Chatbot }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdminUser = !!user && ["admin", "super_admin", "org_admin", "it_admin", "hr_admin", "bot_admin"].includes(user.role || "");
  const [enabledChannels, setEnabledChannels] = useState<string[]>(chatbot.enabledChannels || ["web"]);
  const [smsWebhookUrl, setSmsWebhookUrl] = useState(chatbot.smsWebhookUrl || "");
  const [emailWebhookUrl, setEmailWebhookUrl] = useState(chatbot.emailWebhookUrl || "");

  const { data: installs = [] } = useQuery<ChannelInstallation[]>({
    queryKey: ["/api/chatbots", chatbot.id, "channel-installations"],
  });
  const slackInstall = installs.find((i) => i.type === "slack");
  const teamsInstall = installs.find((i) => i.type === "teams");

  useEffect(() => {
    setEnabledChannels(chatbot.enabledChannels || ["web"]);
    setSmsWebhookUrl(chatbot.smsWebhookUrl || "");
    setEmailWebhookUrl(chatbot.emailWebhookUrl || "");
  }, [chatbot]);

  const updateChannelsMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/chatbots/${chatbot.id}/channels`, {
        enabledChannels,
        smsWebhookUrl: smsWebhookUrl || null,
        emailWebhookUrl: emailWebhookUrl || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id] });
      toast({ title: "Channel settings saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const toggleChannel = (channelId: string) => {
    if (channelId === "web") return;
    setEnabledChannels(prev =>
      prev.includes(channelId)
        ? prev.filter(c => c !== channelId)
        : [...prev, channelId]
    );
  };

  const smsWebhookEndpoint = `${window.location.origin}/api/v1/webhooks/sms?api_key=${chatbot.apiKey}`;
  const emailWebhookEndpoint = `${window.location.origin}/api/v1/webhooks/email?api_key=${chatbot.apiKey}`;

  const { data: channelStats } = useQuery<{ channelBreakdown: Record<string, number>; totalConversations: number }>({
    queryKey: ["/api/chatbots", chatbot.id, "channel-stats"],
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Radio className="w-5 h-5" />Channels
          </CardTitle>
          <CardDescription>Enable or disable communication channels for this chatbot</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {CHANNEL_OPTIONS.map((channel) => (
            <div key={channel.id} className="flex items-center justify-between gap-4 p-3 rounded-md bg-muted/50" data-testid={`channel-${channel.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <channel.icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">{channel.label}</p>
                  <p className="text-xs text-muted-foreground">{channel.description}</p>
                </div>
              </div>
              <Switch
                checked={enabledChannels.includes(channel.id)}
                onCheckedChange={() => toggleChannel(channel.id)}
                disabled={channel.id === "web"}
                data-testid={`switch-channel-${channel.id}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {enabledChannels.includes("sms") && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Smartphone className="w-4 h-4" />SMS Configuration (Twilio)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-800 dark:text-blue-200 space-y-1.5">
              <p className="font-semibold">Setup Steps:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Log into your <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a></li>
                <li>Navigate to Phone Numbers &rarr; Manage &rarr; Active Numbers</li>
                <li>Select your phone number and scroll to "Messaging"</li>
                <li>Set "A message comes in" webhook to the URL below (HTTP POST)</li>
                <li>Save — incoming SMS will now be routed to this chatbot</li>
              </ol>
            </div>
            <div className="space-y-2">
              <Label>Inbound Webhook URL</Label>
              <div className="flex items-center gap-2 p-3 rounded-md bg-muted font-mono text-xs">
                <code className="flex-1 truncate" data-testid="text-sms-webhook-url">{smsWebhookEndpoint}</code>
                <CopyButton text={smsWebhookEndpoint} />
              </div>
              <p className="text-xs text-muted-foreground">Twilio will POST inbound messages in standard format (From, Body). Responses are returned as TwiML.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="input-sms-webhook-url">Outbound SMS Webhook (optional)</Label>
              <Input
                id="input-sms-webhook-url"
                placeholder="https://your-server.com/sms-callback"
                value={smsWebhookUrl}
                onChange={(e) => setSmsWebhookUrl(e.target.value)}
                data-testid="input-sms-webhook-url"
              />
              <p className="text-xs text-muted-foreground">Optional callback URL to receive delivery status updates.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {enabledChannels.includes("email") && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" />Email Configuration (SendGrid/Mailgun)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-800 dark:text-blue-200 space-y-1.5">
              <p className="font-semibold">SendGrid Setup:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Go to <a href="https://app.sendgrid.com/settings/parse" target="_blank" rel="noopener noreferrer" className="underline">SendGrid Inbound Parse</a></li>
                <li>Add a Host & URL — set the destination URL to the webhook below</li>
                <li>Configure your domain's MX records to point to mx.sendgrid.net</li>
                <li>Emails sent to your domain will be forwarded to this chatbot</li>
              </ol>
              <p className="font-semibold mt-2">Mailgun Setup:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Go to Mailgun &rarr; Receiving &rarr; Create Route</li>
                <li>Set expression type to "Match Recipient" with your address</li>
                <li>Set action to "Forward" with the webhook URL below</li>
              </ol>
            </div>
            <div className="space-y-2">
              <Label>Inbound Parse Webhook URL</Label>
              <div className="flex items-center gap-2 p-3 rounded-md bg-muted font-mono text-xs">
                <code className="flex-1 truncate" data-testid="text-email-webhook-url">{emailWebhookEndpoint}</code>
                <CopyButton text={emailWebhookEndpoint} />
              </div>
              <p className="text-xs text-muted-foreground">Accepts standard email fields (from, subject, text/html). Compatible with SendGrid Inbound Parse and Mailgun forwarding.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="input-email-webhook-url">Outbound Email Webhook (optional)</Label>
              <Input
                id="input-email-webhook-url"
                placeholder="https://your-server.com/email-callback"
                value={emailWebhookUrl}
                onChange={(e) => setEmailWebhookUrl(e.target.value)}
                data-testid="input-email-webhook-url"
              />
              <p className="text-xs text-muted-foreground">Optional callback URL for email delivery notifications.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <ChannelInstallCard chatbotId={chatbot.id} type="slack" install={slackInstall} isAdmin={isAdminUser} />
        <ChannelInstallCard chatbotId={chatbot.id} type="teams" install={teamsInstall} isAdmin={isAdminUser} />
      </div>

      {channelStats && channelStats.totalConversations > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" />Channel Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(channelStats.channelBreakdown).sort((a, b) => b[1] - a[1]).map(([ch, count]) => {
                const pct = channelStats.totalConversations > 0 ? (count / channelStats.totalConversations) * 100 : 0;
                const channelInfo = CHANNEL_OPTIONS.find(c => c.id === ch);
                return (
                  <div key={ch} className="flex items-center gap-3" data-testid={`stat-channel-${ch}`}>
                    <span className="text-xs w-16 text-right text-muted-foreground">{channelInfo?.label || ch}</span>
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-12">{count} ({Math.round(pct)}%)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Button onClick={() => updateChannelsMutation.mutate()} disabled={updateChannelsMutation.isPending} data-testid="button-save-channels">
        {updateChannelsMutation.isPending ? "Saving..." : "Save Channel Settings"}
      </Button>
    </div>
  );
}

function BotAccessPanel({ chatbotId }: { chatbotId: string }) {
  const { toast } = useToast();

  // ── org-member access rules ────────────────────────────────────────────────
  const { data: rule, isLoading: ruleLoading } = useQuery<{
    openToAll: string; allowedRoles: string[]; allowedGroupIds: string[]; allowedDepartments: string[];
  }>({ queryKey: ["/api/chatbots", chatbotId, "access"] });
  const { data: groups } = useQuery<{ id: string; name: string; memberCount: number }[]>({ queryKey: ["/api/groups"] });
  const { data: depts }  = useQuery<{ id: string; name: string }[]>({ queryKey: ["/api/departments"] });

  const [openToAll, setOpenToAll]               = useState(true);
  const [allowedRoles, setAllowedRoles]          = useState<string[]>([]);
  const [allowedGroupIds, setAllowedGroupIds]    = useState<string[]>([]);
  const [allowedDepartments, setAllowedDepartments] = useState<string[]>([]);

  useEffect(() => {
    if (rule) {
      setOpenToAll(rule.openToAll === "true");
      setAllowedRoles(rule.allowedRoles || []);
      setAllowedGroupIds(rule.allowedGroupIds || []);
      setAllowedDepartments(rule.allowedDepartments || []);
    }
  }, [rule]);

  // ── full chatbot data for visitor settings ─────────────────────────────────
  const { data: bot, isLoading: botLoading } = useQuery<{
    accessMode?: string;
    allowedDomains?: string[];
    visitorRateLimitPerMinute?: number | null;
    apiKey?: string;
  }>({ queryKey: ["/api/chatbots", chatbotId] });

  const [accessMode, setAccessMode]   = useState("authenticated");
  const [domains, setDomains]         = useState<string[]>([]);
  const [newDomain, setNewDomain]     = useState("");
  const [rateLimit, setRateLimit]     = useState("");
  const [regenOpen, setRegenOpen]     = useState(false);
  const [freshApiKey, setFreshApiKey] = useState<string | null>(null);

  useEffect(() => {
    if (bot) {
      setAccessMode(bot.accessMode || "authenticated");
      setDomains(bot.allowedDomains || []);
      setRateLimit(bot.visitorRateLimitPerMinute != null ? String(bot.visitorRateLimitPerMinute) : "");
    }
  }, [bot]);

  // ── mutations ──────────────────────────────────────────────────────────────
  const invalidateBot = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId] });
    queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
  };

  const saveVisitor = useMutation({
    mutationFn: async () => {
      const rl = rateLimit.trim() === "" ? 0 : parseInt(rateLimit, 10);
      await apiRequest("PATCH", `/api/chatbots/${chatbotId}`, {
        accessMode,
        visitorRateLimitPerMinute: isNaN(rl) ? 0 : rl,
      });
    },
    onSuccess: () => { invalidateBot(); toast({ title: "Visitor settings saved" }); },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const saveDomains = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", `/api/chatbots/${chatbotId}`, { allowedDomains: domains }),
    onSuccess: () => { invalidateBot(); toast({ title: "Domain restrictions saved" }); },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const regenKey = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/chatbots/${chatbotId}/regenerate-key`);
      return res.json() as Promise<{ apiKey: string }>;
    },
    onSuccess: (data) => {
      setFreshApiKey(data.apiKey);
      setRegenOpen(false);
      invalidateBot();
      toast({ title: "API key regenerated", description: "The old key is immediately invalid.", variant: "destructive" });
    },
    onError: (e: any) => toast({ title: "Regeneration failed", description: e.message, variant: "destructive" }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/chatbots/${chatbotId}/access`, {
        openToAll, allowedRoles, allowedGroupIds, allowedDepartments,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "access"] });
      invalidateBot();
      toast({ title: "Access rules saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  // ── helpers ────────────────────────────────────────────────────────────────
  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
    if (!d || domains.includes(d)) { setNewDomain(""); return; }
    setDomains([...domains, d]);
    setNewDomain("");
  };

  const maskedKey = (key: string) =>
    key ? key.slice(0, 14) + "••••••••••••••••" : "sk-bot-••••••••••••••••";

  const rlNum = parseInt(rateLimit, 10);
  const rlValid = rateLimit === "" || (!isNaN(rlNum) && rlNum >= 1 && rlNum <= 1000);

  const roleOptions = [
    { value: "employee", label: "Employee" },
    { value: "manager",  label: "Manager"  },
    { value: "hr_admin", label: "HR Admin" },
    { value: "it_admin", label: "IT Admin" },
    { value: "bot_admin",label: "Bot Admin"},
    { value: "org_admin",label: "Org Admin"},
    { value: "user",     label: "User (legacy)" },
  ];
  const toggle = <T extends string>(arr: T[], val: T, set: (v: T[]) => void) =>
    arr.includes(val) ? set(arr.filter(x => x !== val)) : set([...arr, val]);

  if (ruleLoading || botLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">

      {/* ── 1. Access Mode + Rate Limit ───────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Shield className="w-5 h-5" />Access Mode</CardTitle>
          <CardDescription>Controls who can initiate a conversation with this bot.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setAccessMode("authenticated")}
              className={`p-4 rounded-lg border text-left transition-all ${(accessMode === "authenticated" || accessMode === "login_required") ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
              data-testid="button-mode-login-required"
            >
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">Login Required</span>
              </div>
              <p className="text-xs text-muted-foreground">Only signed-in org members can chat.</p>
            </button>
            <button
              type="button"
              onClick={() => setAccessMode("public")}
              className={`p-4 rounded-lg border text-left transition-all ${accessMode === "public" ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
              data-testid="button-mode-public"
            >
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">Public</span>
              </div>
              <p className="text-xs text-muted-foreground">Anyone with the link can chat anonymously.</p>
            </button>
            <button
              type="button"
              onClick={() => setAccessMode("api_key_only")}
              className={`p-4 rounded-lg border text-left transition-all ${accessMode === "api_key_only" ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
              data-testid="button-mode-api-key-only"
            >
              <div className="flex items-center gap-2 mb-2">
                <Code className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">API Key Only</span>
              </div>
              <p className="text-xs text-muted-foreground">Server-to-server via bot API key. No login required.</p>
            </button>
          </div>
          {accessMode === "api_key_only" && (
            <div className="p-3 rounded-md bg-muted/50 border text-xs text-muted-foreground">
              Callers must include <code className="font-mono bg-muted px-1 rounded">Authorization: Bearer &lt;bot-api-key&gt;</code> in every request. No domain allow-list is enforced.
            </div>
          )}

          <div className="pt-3 border-t space-y-2">
            <Label htmlFor="input-rate-limit" className="text-sm font-medium flex items-center gap-2">
              <Zap className="w-4 h-4" /> Visitor Rate Limit
            </Label>
            <p className="text-xs text-muted-foreground">Maximum messages per visitor per minute. Leave empty for no limit (range 1–1000).</p>
            <div className="flex items-center gap-3">
              <Input
                id="input-rate-limit"
                type="number"
                min={1}
                max={1000}
                value={rateLimit}
                onChange={e => setRateLimit(e.target.value)}
                placeholder="No limit"
                className="w-36"
                data-testid="input-visitor-rate-limit"
              />
              {!rlValid && <p className="text-xs text-destructive">Must be between 1 and 1000</p>}
              {rateLimit !== "" && rlValid && (
                <p className="text-xs text-muted-foreground">Max {rateLimit} msg/min per visitor</p>
              )}
            </div>
          </div>

          <Button
            onClick={() => saveVisitor.mutate()}
            disabled={saveVisitor.isPending || !rlValid}
            data-testid="button-save-visitor-settings"
          >
            {saveVisitor.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      {/* ── 2. Allowed Domains ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Globe className="w-5 h-5" />Allowed Domains</CardTitle>
          <CardDescription>Restrict which websites may embed this chatbot. Leave empty to allow all domains.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={newDomain}
              onChange={e => setNewDomain(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addDomain(); } }}
              placeholder="example.com"
              className="font-mono"
              data-testid="input-new-domain"
            />
            <Button type="button" variant="outline" onClick={addDomain} disabled={!newDomain.trim()} data-testid="button-add-domain">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {domains.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="list-allowed-domains">
              {domains.map((d, i) => (
                <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono border bg-muted" data-testid={`chip-domain-${i}`}>
                  {d}
                  <button
                    type="button"
                    onClick={() => setDomains(domains.filter(x => x !== d))}
                    className="ml-1 opacity-60 hover:opacity-100"
                    aria-label={`Remove ${d}`}
                    data-testid={`button-remove-domain-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No restrictions — widget works on all domains.</p>
          )}
          <Button onClick={() => saveDomains.mutate()} disabled={saveDomains.isPending} data-testid="button-save-domains">
            {saveDomains.isPending ? "Saving…" : "Save Domain Restrictions"}
          </Button>
        </CardContent>
      </Card>

      {/* ── 3. API Key ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Code2 className="w-5 h-5" />API Key</CardTitle>
          <CardDescription>Used to authenticate direct API calls and embed scripts. Keep this secret.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {freshApiKey ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700">
                <code className="font-mono text-sm flex-1 break-all" data-testid="text-fresh-api-key">{freshApiKey}</code>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(freshApiKey); toast({ title: "Copied!" }); }}
                  className="shrink-0" aria-label="Copy new API key"
                >
                  <Copy className="w-4 h-4 opacity-60 hover:opacity-100" />
                </button>
              </div>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">⚠ Copy this key now — it won't be shown in full again.</p>
              <Button variant="outline" size="sm" onClick={() => setFreshApiKey(null)} data-testid="button-dismiss-fresh-key">Dismiss</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
              <code className="font-mono text-sm text-muted-foreground flex-1" data-testid="text-masked-api-key">
                {maskedKey(bot?.apiKey || "")}
              </code>
            </div>
          )}
          <Button variant="outline" onClick={() => setRegenOpen(true)} disabled={regenKey.isPending} data-testid="button-regenerate-api-key">
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Key
          </Button>
          <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
            <DialogContent data-testid="dialog-confirm-regen">
              <DialogHeader>
                <DialogTitle>Regenerate API Key?</DialogTitle>
                <DialogDescription>
                  The current key will be <strong>immediately invalid</strong>. Any embed scripts or integrations using it will stop working until updated.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRegenOpen(false)} disabled={regenKey.isPending}>Cancel</Button>
                <Button variant="destructive" onClick={() => regenKey.mutate()} disabled={regenKey.isPending} data-testid="button-confirm-regen">
                  {regenKey.isPending ? "Regenerating…" : "Yes, regenerate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* ── 4. Bot Access Control (org-member restriction, unchanged) ─────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><User className="w-5 h-5" />Bot Access Control</CardTitle>
          <CardDescription>Restrict which staff members can use this bot. Used by internal-mode chat clients and the platform UI.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <p className="text-sm font-medium">Open to all org members</p>
              <p className="text-xs text-muted-foreground">When on, every active user in your organization can use this bot.</p>
            </div>
            <Switch checked={openToAll} onCheckedChange={setOpenToAll} data-testid="switch-open-to-all" />
          </div>

          {!openToAll && (
            <>
              <div>
                <Label className="text-sm font-medium">Allowed Roles</Label>
                <p className="text-xs text-muted-foreground mb-2">Users with any of these roles can access the bot.</p>
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map(r => {
                    const active = allowedRoles.includes(r.value);
                    return (
                      <button key={r.value} type="button"
                        onClick={() => toggle(allowedRoles, r.value, setAllowedRoles)}
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                        data-testid={`chip-role-${r.value}`}
                      >{r.label}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Allowed Groups</Label>
                <p className="text-xs text-muted-foreground mb-2">Members of any selected group can access. Manage groups under Workforce.</p>
                <div className="flex flex-wrap gap-2">
                  {(!groups || groups.length === 0) ? (
                    <p className="text-xs text-muted-foreground italic">No groups created yet.</p>
                  ) : groups.map(g => {
                    const active = allowedGroupIds.includes(g.id);
                    return (
                      <button key={g.id} type="button"
                        onClick={() => toggle(allowedGroupIds, g.id, setAllowedGroupIds)}
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                        data-testid={`chip-group-${g.id}`}
                      >{g.name} <span className="opacity-60">({g.memberCount})</span></button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Allowed Departments</Label>
                <p className="text-xs text-muted-foreground mb-2">Users assigned to any of these departments can access.</p>
                <div className="flex flex-wrap gap-2">
                  {(!depts || depts.length === 0) ? (
                    <p className="text-xs text-muted-foreground italic">No departments yet.</p>
                  ) : depts.map(d => {
                    const active = allowedDepartments.includes(d.name);
                    return (
                      <button key={d.id} type="button"
                        onClick={() => toggle(allowedDepartments, d.name, setAllowedDepartments)}
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                        data-testid={`chip-dept-${d.id}`}
                      >{d.name}</button>
                    );
                  })}
                </div>
              </div>
              {allowedRoles.length === 0 && allowedGroupIds.length === 0 && allowedDepartments.length === 0 && (
                <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-200">
                  ⚠ With no roles, groups, or departments selected, only org admins will be able to use this bot.
                </div>
              )}
            </>
          )}

          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-access">
            {save.isPending ? "Saving..." : "Save access rules"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Knowledge sources progress card — extracted so it can be reused both in
// the persistent right rail AND in the Appearance tab right column (where
// it sits below the Live Preview in a single stacked column).
function KnowledgeSourcesCard({ chatbot, onJump }: { chatbot: Chatbot; onJump: (id: string) => void }) {
  const { data: sources } = useQuery<KnowledgeSource[]>({
    queryKey: ["/api/chatbots", chatbot.id, "knowledge"],
    enabled: !!chatbot.id,
  });
  const sourceCount = sources?.length ?? 0;
  const indexedCount = sources?.filter(
    (s) => (s.status ?? "").toLowerCase() === "indexed" || (s.status ?? "").toLowerCase() === "ready"
  ).length ?? 0;
  const indexedPct = sourceCount ? Math.round((indexedCount / sourceCount) * 100) : 0;

  return (
    <Card>
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <div className="text-sm font-semibold">Knowledge sources</div>
        <button
          type="button"
          onClick={() => onJump("knowledge")}
          className="text-[11px] font-medium text-primary hover:underline"
          data-testid="link-rail-add-knowledge"
        >
          + Add
        </button>
      </div>
      <div className="px-4 py-3 space-y-2">
        {sourceCount === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="text-rail-no-sources">
            No sources yet — upload a PDF, paste a URL, or add raw text.
          </p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-semibold tabular-nums" data-testid="text-rail-indexed-count">
                {indexedCount}
              </div>
              <div className="text-xs text-muted-foreground">
                of {sourceCount} indexed
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden>
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${indexedPct}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {indexedPct}% indexed and searchable.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

// Channels & integrations jump-links card — extracted alongside
// KnowledgeSourcesCard for reuse in the Appearance tab right column.
function ChannelsIntegrationsCard({ chatbot, onJump }: { chatbot: Chatbot; onJump: (id: string) => void }) {
  return (
    <Card>
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <div className="text-sm font-semibold">Channels &amp; integrations</div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Jump to</span>
      </div>
      <div className="px-2 py-2 space-y-0.5 text-sm">
        <button
          type="button"
          onClick={() => onJump("channels")}
          className="w-full flex items-center justify-between gap-2 hover:bg-muted/60 rounded-md px-2.5 py-2 transition-colors text-left"
          data-testid="link-rail-channels"
        >
          <span className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-muted-foreground" />
            Channels
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          type="button"
          onClick={() => onJump("webhooks")}
          className="w-full flex items-center justify-between gap-2 hover:bg-muted/60 rounded-md px-2.5 py-2 transition-colors text-left"
          data-testid="link-rail-webhooks"
        >
          <span className="flex items-center gap-2">
            <Webhook className="w-4 h-4 text-muted-foreground" />
            Webhooks
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <Link
          href={`/chatbots/${chatbot.id}/tools`}
          className="w-full flex items-center justify-between gap-2 hover:bg-muted/60 rounded-md px-2.5 py-2 transition-colors"
          data-testid="link-rail-tools"
        >
          <span className="flex items-center gap-2">
            <Plug className="w-4 h-4 text-muted-foreground" />
            Tools &amp; integrations
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </Link>
        <button
          type="button"
          onClick={() => onJump("api")}
          className="w-full flex items-center justify-between gap-2 hover:bg-muted/60 rounded-md px-2.5 py-2 transition-colors text-left"
          data-testid="link-rail-api"
        >
          <span className="flex items-center gap-2">
            <Code2 className="w-4 h-4 text-muted-foreground" />
            API &amp; embed
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
    </Card>
  );
}

// Persistent right rail mirroring the BotCMS reference: knowledge progress
// on top, channel/tool jump-links below. The Appearance tab additionally
// inlines these two cards beneath its Live Preview so the user sees the
// three things stacked in a single column.
function BotRightRail({ chatbot, onJump }: { chatbot: Chatbot; onJump: (id: string) => void }) {
  return (
    <aside className="hidden xl:block w-[340px] flex-shrink-0" data-testid="bot-right-rail">
      <div className="sticky top-4 space-y-4">
        <KnowledgeSourcesCard chatbot={chatbot} onJump={onJump} />
        <ChannelsIntegrationsCard chatbot={chatbot} onJump={onJump} />
      </div>
    </aside>
  );
}

export default function ChatbotDetail() {
  const [, params] = useRoute("/chatbots/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, organization } = useAuth();
  const botId = params?.id;
  // Admin-only controls (status toggle, etc) follow the same role list used
  // throughout the app (see ChannelsPanel ~line 848). Keep both in sync.
  const isAdminUser = !!user && ["admin", "super_admin", "org_admin", "it_admin", "hr_admin", "bot_admin"].includes(user.role || "");

  const { data: allProviderModels } = useQuery<ProviderModels[]>({
    queryKey: ["/api/chatbots", botId, "all-models"],
    enabled: !!botId,
  });

  const detectedModelsList = useMemo(() => {
    if (allProviderModels && allProviderModels.length > 0) {
      return allProviderModels.flatMap(pm => pm.models.filter(m => isChatModel(pm.provider, m)));
    }
    return [];
  }, [allProviderModels]);

  const hasApiKey = !!(allProviderModels && allProviderModels.length > 0);

  const { data: chatbot, isLoading } = useQuery<Chatbot>({
    queryKey: ["/api/chatbots", botId],
    enabled: !!botId,
  });

  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("website");
  const [editPrompt, setEditPrompt] = useState("");
  const [editWelcome, setEditWelcome] = useState("");
  const [editEndMessage, setEditEndMessage] = useState("");
  const [editScopeDescription, setEditScopeDescription] = useState("");
  const [editDisplayMode, setEditDisplayMode] = useState("widget");
  // accessMode = "authenticated" (default) requires the end-user to be
  // signed in to chat. "public" lets anyone with the bot's apiKey chat
  // without logging in (legacy / external-facing bots only).
  const [editAccessMode, setEditAccessMode] = useState("authenticated");
  const [editSupervisorEnabled, setEditSupervisorEnabled] = useState(false);
  const [editSupervisorPrompt, setEditSupervisorPrompt] = useState("");
  // Pre-chat contact form (Name / Email / Phone). Opt-in for authenticated
  // bots (off by default); always on and enforced for public bots.
  const [editPreChatForm, setEditPreChatForm] = useState(false);
  const [editModel, setEditModel] = useState("");
  const [editTemp, setEditTemp] = useState(7);
  const [editMaxTokens, setEditMaxTokens] = useState(2048);
  const [editAllowGeneralKnowledge, setEditAllowGeneralKnowledge] = useState(false);
  const [editColor, setEditColor] = useState("#0c3547");
  const [editTheme, setEditTheme] = useState("professional-blue");
  // Per-bot color overrides. Empty string = "no override" (the inline
  // preview and chat-preview will fall back to the active theme's value).
  const [editBackgroundColor, setEditBackgroundColor] = useState("");
  const [editBotBubbleColor, setEditBotBubbleColor] = useState("");
  const [editBotTextColor, setEditBotTextColor] = useState("");
  const [editUserBubbleColor, setEditUserBubbleColor] = useState("");
  const [editUserTextColor, setEditUserTextColor] = useState("");
  const [editBotIcon, setEditBotIcon] = useState("bot");
  const [editDomains, setEditDomains] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionCategory, setNewQuestionCategory] = useState("General");
  const [questionFilter, setQuestionFilter] = useState<string>("all");
  const [activeSection, setActiveSection] = useState("settings");
  const [editVoiceMode, setEditVoiceMode] = useState("inherit");
  const [editVoiceTone, setEditVoiceTone] = useState("");
  const [editVoicePreferredTerms, setEditVoicePreferredTerms] = useState("");
  const [editVoiceProhibitedTerms, setEditVoiceProhibitedTerms] = useState("");
  const [editVoiceReadingLevel, setEditVoiceReadingLevel] = useState("");
  const [editVoiceInclusivityStandard, setEditVoiceInclusivityStandard] = useState("");

  const models = useMemo(() => {
    const list = detectedModelsList.map(m => ({ value: m, label: m }));
    return list;
  }, [detectedModelsList, chatbot?.model]);

  useEffect(() => {
    if (chatbot) {
      setEditName(chatbot.name);
      setEditType(chatbot.type || "website");
      setEditPrompt(chatbot.systemPrompt || "");
      setEditWelcome(chatbot.welcomeMessage || "");
      setEditEndMessage(chatbot.endMessage || "Thank you for chatting with us! Please rate your experience.");
      setEditScopeDescription(chatbot.scopeDescription || "");
      setEditDisplayMode(chatbot.displayMode || "widget");
      setEditAccessMode((chatbot as any).accessMode || "authenticated");
      setEditSupervisorEnabled(chatbot.supervisorEnabled === "true");
      setEditSupervisorPrompt(chatbot.supervisorPrompt || "");
      setEditPreChatForm(!!(chatbot as any).preChatFormEnabled);
      setEditModel(chatbot.model || "");
      setEditTemp(chatbot.temperature ?? 7);
      setEditMaxTokens(chatbot.maxTokens ?? 2048);
      setEditAllowGeneralKnowledge(!!(chatbot as any).allowGeneralKnowledge);
      setEditColor(chatbot.primaryColor || "#0c3547");
      setEditTheme(chatbot.theme || "professional-blue");
      setEditBackgroundColor((chatbot as any).backgroundColor || "");
      setEditBotBubbleColor((chatbot as any).botBubbleColor || "");
      setEditBotTextColor((chatbot as any).botTextColor || "");
      setEditUserBubbleColor((chatbot as any).userBubbleColor || "");
      setEditUserTextColor((chatbot as any).userTextColor || "");
      setEditBotIcon(chatbot.botIcon || "bot");
      setEditDomains((chatbot.allowedDomains || []).join("\n"));
      setEditVoiceMode((chatbot as any).voiceMode || "inherit");
      setEditVoiceTone((chatbot as any).voiceTone || "");
      setEditVoicePreferredTerms((chatbot as any).voicePreferredTerms || "");
      setEditVoiceProhibitedTerms((chatbot as any).voiceProhibitedTerms || "");
      setEditVoiceReadingLevel((chatbot as any).voiceReadingLevel || "");
      setEditVoiceInclusivityStandard((chatbot as any).voiceInclusivityStandard || "");
    }
  }, [chatbot]);

  // The base theme (used by the picker grid swatches and as the fallback
  // for empty per-color override fields). Don't apply overrides here —
  // the override input swatches need to show the underlying theme color
  // when the user hasn't picked an override yet.
  const currentTheme = getThemeById(editTheme);
  // What the live preview should actually render with: the chosen theme
  // with any per-bot color overrides applied on top.
  const { theme: previewTheme } = resolveThemeWithContrast({
    theme: editTheme,
    primaryColor: editColor,
    backgroundColor: editBackgroundColor || null,
    botBubbleColor: editBotBubbleColor || null,
    botTextColor: editBotTextColor || null,
    userBubbleColor: editUserBubbleColor || null,
    userTextColor: editUserTextColor || null,
  });

  const { data: predefinedQuestions } = useQuery<any[]>({
    queryKey: ["/api/chatbots", botId, "predefined-questions"],
    enabled: !!botId,
  });

  // Categories are defined per-bot simply by being assigned to questions —
  // there is no fixed list. We surface the categories this bot already uses
  // as suggestions, plus a few common defaults to seed an empty bot.
  const groupedQuestions = (predefinedQuestions || []).reduce<Record<string, any[]>>((acc, q: any) => {
    const cat = (q.category || "General").trim() || "General";
    (acc[cat] ||= []).push(q);
    return acc;
  }, {});
  const questionCategories = Array.from(
    new Set([...Object.keys(groupedQuestions), "General", "Support", "Billing", "Technical"])
  );

  const addQuestionMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/chatbots/${botId}/predefined-questions`, {
        question: newQuestion,
        category: newQuestionCategory.trim() || "General",
        sortOrder: (predefinedQuestions?.length ?? 0) + 1,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", botId, "predefined-questions"] });
      setNewQuestion("");
      toast({ title: "Question added" });
    },
  });

  const restorePredefinedQuestion = async (id: string) => {
    try {
      await apiRequest("POST", `/api/chatbots/${botId}/predefined-questions/${id}/restore`);
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", botId, "predefined-questions"] });
      toast({ title: "Question restored" });
    } catch (err: any) {
      toast({ title: "Restore failed", description: err?.message, variant: "destructive" });
    }
  };

  const deleteQuestionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/chatbots/${botId}/predefined-questions/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", botId, "predefined-questions"] });
      toast({
        title: "Question archived",
        action: (
          <ToastAction altText="Undo — restore archived question" onClick={() => restorePredefinedQuestion(id)} data-testid="button-undo-archive-question">
            Undo
          </ToastAction>
        ),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const domains = editDomains.split("\n").map(d => d.trim()).filter(Boolean);
      await apiRequest("PATCH", `/api/chatbots/${botId}`, {
        name: editName,
        type: editType,
        systemPrompt: editPrompt,
        welcomeMessage: editWelcome,
        endMessage: editEndMessage,
        scopeDescription: editScopeDescription,
        displayMode: editDisplayMode,
        accessMode: editAccessMode,
        supervisorEnabled: editSupervisorEnabled ? "true" : "false",
        supervisorPrompt: editSupervisorPrompt,
        preChatFormEnabled: editPreChatForm,
        model: editModel,
        temperature: editTemp,
        maxTokens: editMaxTokens,
        allowGeneralKnowledge: editAllowGeneralKnowledge,
        primaryColor: editColor,
        theme: editTheme,
        // Send empty overrides as null so the DB stores NULL and the
        // resolveTheme() helper falls back to the active theme's value.
        backgroundColor: editBackgroundColor || null,
        botBubbleColor: editBotBubbleColor || null,
        botTextColor: editBotTextColor || null,
        userBubbleColor: editUserBubbleColor || null,
        userTextColor: editUserTextColor || null,
        botIcon: editBotIcon,
        allowedDomains: domains.length > 0 ? domains : null,
        voiceMode: editVoiceMode,
        voiceTone: editVoiceTone,
        voicePreferredTerms: editVoicePreferredTerms,
        voiceProhibitedTerms: editVoiceProhibitedTerms,
        voiceReadingLevel: editVoiceReadingLevel,
        voiceInclusivityStandard: editVoiceInclusivityStandard,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", botId] });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      toast({ title: "Settings saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const restoreChatbot = async (id: string) => {
    try {
      await apiRequest("POST", `/api/chatbots/${id}/restore`);
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", id] });
      toast({ title: "Chatbot restored" });
    } catch (err: any) {
      toast({ title: "Restore failed", description: err?.message, variant: "destructive" });
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/chatbots/${botId}`);
    },
    onSuccess: () => {
      // Capture the archived bot ID before navigating away so Undo can restore
      // it. The DELETE above already required a defined botId.
      const archivedBotId = botId!;
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      toast({
        title: "Chatbot archived",
        description: "Its conversations and history are retained.",
        action: (
          <ToastAction altText="Undo — restore archived chatbot" onClick={() => restoreChatbot(archivedBotId)} data-testid="button-undo-archive-bot">
            Undo
          </ToastAction>
        ),
      });
      setLocation("/chatbots");
    },
  });

  // Status toggle. Server enforces the plan's enabled-bot cap on the
  // inactive→active transition (PATCH /api/chatbots/:id returns 403
  // BOT_LIMIT_REACHED), so the UI just needs to surface that error nicely
  // and refresh the list so the new state shows up everywhere.
  const statusMutation = useMutation({
    mutationFn: async (nextStatus: "active" | "inactive") => {
      const token = getAuthToken();
      const res = await fetch(`/api/chatbots/${botId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err: any = new Error(body.message || "Failed to update status");
        err.code = body.code;
        throw err;
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", botId] });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
    },
    onError: (err: any) => {
      toast({
        title: err.code === "BOT_LIMIT_REACHED" ? "Plan limit reached" : "Could not update status",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-6" aria-busy="true">
        <p className="sr-only" role="status">Loading bot details…</p>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!chatbot) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-muted-foreground">Chatbot not found</p>
      </div>
    );
  }

  const TypeIcon = getBotTypeIcon(chatbot.type);
  const apiEndpoint = `${window.location.origin}/api/v1/chat`;

  // Validate the bot's brand colour before pushing it into inline styles —
  // bad data (null, "", non-hex) would otherwise render as a transparent
  // button. Also pick a readable foreground based on perceived luminance
  // so very light brand colours don't render white-on-white.
  const rawBrand = (chatbot.primaryColor || "").trim();
  const brandAccent = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(rawBrand) ? rawBrand : "#0c3547";
  const brandFg = (() => {
    const m = brandAccent.replace("#", "");
    const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    // Rec. 709 luma — pick dark text on light brand colours.
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 170 ? "#0b1220" : "#ffffff";
  })();
  return (
    <div className="p-6 pb-28 max-w-[1600px] mx-auto space-y-5">
      {/* BotCMS-style action bar. flex-wrap lets the status toggle and the
          Save & publish button drop to a second row at 320px instead of
          overflowing off-screen (WCAG 1.4.10 reflow). */}
      <div className="glass-panel rounded-xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bot-detail-header">
        <Link href="/chatbots">
          <Button variant="ghost" size="icon" aria-label="Back to chatbots" data-testid="button-back-detail">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
        </Link>
        {/* basis-[13rem]: below that width the toggle/save wrap to their own
            row instead of crushing the title into a 1-character column. */}
        <div className="flex items-center gap-3 flex-1 min-w-0 basis-[13rem]">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg"
            style={{
              backgroundColor: brandAccent,
              boxShadow: `0 8px 24px -8px ${brandAccent}80`,
            }}
          >
            <TypeIcon className="w-5 h-5" style={{ color: readableTextOn(brandAccent) }} />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold tracking-tight break-words sm:truncate leading-tight" data-testid="text-bot-detail-name">{chatbot.name}</h1>
            <p className="text-[11px] text-muted-foreground capitalize mt-1 break-words">{chatbot.type} chatbot · {editModel || "no model"}</p>
          </div>
        </div>
        <span className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium brand-pill">
          <span className="w-1.5 h-1.5 rounded-full bg-current" /> Live · v2.4
        </span>
        {isAdminUser && (
          <div className="flex items-center gap-2 sm:pl-1 sm:border-l border-border/60" data-testid="control-bot-status">
            <Switch
              checked={chatbot.status === "active"}
              disabled={statusMutation.isPending}
              onCheckedChange={(checked) => statusMutation.mutate(checked ? "active" : "inactive")}
              data-testid="switch-bot-status"
            />
            <Badge variant={chatbot.status === "active" ? "default" : "secondary"} data-testid="badge-bot-status">
              {chatbot.status === "active" ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        )}
        <Button
          size="sm"
          onClick={() => updateMutation.mutate()}
          disabled={updateMutation.isPending}
          className="shadow-md"
          style={{ backgroundColor: brandAccent, color: brandFg }}
          data-testid="button-header-save"
        >
          <Check className="w-4 h-4 mr-1" />
          {updateMutation.isPending ? "Saving…" : "Save & publish"}
        </Button>
      </div>

      {/* Mobile section switcher — directly under the header so navigation is
          never "lost" below the summary cards at 320px. Hidden at lg+ where
          the sidebar rail takes over. */}
      <div className="lg:hidden glass-panel rounded-xl p-3 space-y-1.5">
        <Label htmlFor="select-bot-section">Configuration section</Label>
        <Select
          value={activeSection}
          onValueChange={(v) => {
            if (v === "tools") setLocation(`/chatbots/${chatbot.id}/tools`);
            else if (v === "integrations") setLocation(`/chatbots/${chatbot.id}/integrations`);
            else if (v === "history") setLocation(`/chatbots/${chatbot.id}/history`);
            else if (v === "staff-portal") window.open(`/portal/${chatbot.apiKey}`, "_blank", "noopener,noreferrer");
            else setActiveSection(v);
          }}
        >
          <SelectTrigger
            id="select-bot-section"
            data-testid="select-bot-section"
            className="w-full max-w-full min-w-0"
          >
            <SelectValue placeholder="Choose a section" />
          </SelectTrigger>
          {/* Popper position gives Radix collision detection: flips up when
              space below is short, clamps height to the viewport via
              --radix-select-content-available-height, and scrolls the
              selected item into view. collisionPadding keeps 8px clearance
              from viewport edges; overscroll-contain stops scroll chaining. */}
          <SelectContent position="popper" collisionPadding={8} className="overscroll-contain">
            <SelectGroup>
              <SelectLabel>1. Configure</SelectLabel>
              <SelectItem value="settings">General</SelectItem>
              <SelectItem value="appearance">Appearance</SelectItem>
              <SelectItem value="questions">Questions</SelectItem>
              <SelectItem value="access">Access</SelectItem>
              <SelectItem value="domains">Domains</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>2. Knowledge</SelectLabel>
              <SelectItem value="knowledge">Sources</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>3. Extend</SelectLabel>
              <SelectItem value="tools">Tools</SelectItem>
              {isAdminUser && <SelectItem value="integrations">Integrations</SelectItem>}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>4. Channels</SelectLabel>
              <SelectItem value="channels">Configuration</SelectItem>
              <SelectItem value="webhooks">Webhooks</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>5. Deploy</SelectLabel>
              <SelectItem value="preview">Preview</SelectItem>
              <SelectItem value="api">API & Embed</SelectItem>
              <SelectItem value="staff-portal">Staff Portal (opens in new tab)</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>6. Monitor</SelectLabel>
              <SelectItem value="reports">Reports</SelectItem>
              <SelectItem value="ratings">Ratings</SelectItem>
              <SelectItem value="live-chat">Live Chat</SelectItem>
              {hasPermission(user, "conversations.view") && <SelectItem value="history">Chat History</SelectItem>}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Stats strip — read-only chips so admins see the bot at a glance */}
      <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-4 gap-3" data-testid="bot-detail-stats">
        <div className="stat-chip px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Status</div>
          <div className="mt-1 text-lg font-semibold capitalize">{chatbot.status}</div>
        </div>
        <div className="stat-chip px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Model</div>
          <div className="mt-1 text-lg font-semibold break-words">{editModel || "—"}</div>
        </div>
        <div className="stat-chip px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Access</div>
          {/* Read live server state, not the edit-form draft — the draft only
              syncs when the form is (re)hydrated and went stale after saves. */}
          <div className="mt-1 text-lg font-semibold capitalize break-words" data-testid="stat-access-mode">{(chatbot as any).accessMode || "authenticated"}</div>
        </div>
        <div className="stat-chip px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Theme</div>
          <div className="mt-1 text-lg font-semibold break-words">{currentTheme?.name || editTheme}</div>
        </div>
      </div>

      {/* Below lg the split-pane collapses to a single column: the sidebar
          rail is replaced by a labelled native select so only one panel is
          in the viewport at 320px (WCAG 1.4.10 reflow). */}
      <div className="flex flex-col lg:flex-row gap-5">
        <nav className="hidden lg:block w-56 flex-shrink-0" data-testid="bot-nav">
          <div
            className="sticky top-4 space-y-1 glass-panel rounded-xl p-3"
            role="tablist"
            aria-orientation="vertical"
            aria-label="Bot configuration sections"
            // Roving tabindex lives on the tab items; the container itself is
            // programmatically focusable only, satisfying the lint rule for
            // the keydown handler without adding a tab stop.
            tabIndex={-1}
            onKeyDown={navRailKeyDown}
          >
            {/* 1. Configure the bot's identity & behaviour first. */}
            <NavGroup label="1. Configure" icon={Settings}>
              <NavItem id="settings" icon={Settings} label="General" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="appearance" icon={Palette} label="Appearance" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="questions" icon={HelpCircle} label="Questions" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="access" icon={User} label="Access" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="domains" icon={Shield} label="Domains" activeSection={activeSection} onClick={setActiveSection} />
            </NavGroup>
            {/* 2. Feed it knowledge. */}
            <NavGroup label="2. Knowledge" icon={BookOpen}>
              <NavItem id="knowledge" icon={FileText} label="Sources" activeSection={activeSection} onClick={setActiveSection} />
            </NavGroup>
            {/* 3. Extend with tools / 3rd-party integrations. */}
            <NavGroup label="3. Extend" icon={Plug}>
              <NavItem id="tools" icon={Settings} label="Tools" activeSection={activeSection} onClick={setActiveSection} href={`/chatbots/${chatbot.id}/tools`} />
              {isAdminUser && (
                <NavItem id="integrations" icon={Plug} label="Integrations" activeSection={activeSection} onClick={setActiveSection} href={`/chatbots/${chatbot.id}/integrations`} />
              )}
            </NavGroup>
            {/* 4. Wire up channels & webhooks. */}
            <NavGroup label="4. Channels" icon={Radio}>
              <NavItem id="channels" icon={Radio} label="Configuration" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="webhooks" icon={Webhook} label="Webhooks" activeSection={activeSection} onClick={setActiveSection} />
            </NavGroup>
            {/* 5. Test & deploy. */}
            <NavGroup label="5. Deploy" icon={Rocket}>
              <NavItem id="preview" icon={Eye} label="Preview" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="api" icon={Code2} label="API & Embed" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="staff-portal" icon={ShieldCheck} label="Staff Portal" activeSection={activeSection} onClick={setActiveSection} href={`/portal/${chatbot.apiKey}`} external />
            </NavGroup>
            {/* 6. Monitor in production. */}
            <NavGroup label="6. Monitor" icon={Activity}>
              <NavItem id="reports" icon={BarChart3} label="Reports" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="ratings" icon={Star} label="Ratings" activeSection={activeSection} onClick={setActiveSection} />
              <NavItem id="live-chat" icon={Headphones} label="Live Chat" activeSection={activeSection} onClick={setActiveSection} />
              {hasPermission(user, "conversations.view") && (
                <NavItem id="history" icon={MessageSquare} label="Chat History" activeSection={activeSection} onClick={setActiveSection} href={`/chatbots/${chatbot.id}/history`} />
              )}
            </NavGroup>
          </div>
        </nav>

        <div
          className="flex-1 min-w-0"
          id="bot-section-panel"
          role="tabpanel"
          aria-labelledby={`tab-${activeSection}`}
        >
        {activeSection === "settings" && <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="input-edit-name">Name</Label>
                <Input id="input-edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="input-edit-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="select-edit-type">Bot Type</Label>
                <Select value={editType} onValueChange={setEditType}>
                  <SelectTrigger id="select-edit-type" data-testid="select-edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website — crawl & answer from website content</SelectItem>
                    <SelectItem value="document">Document — answer from uploaded files (PDF, TXT, CSV)</SelectItem>
                    <SelectItem value="combo">Combo — websites + uploaded documents</SelectItem>
                    <SelectItem value="ai-enhanced">AI-Enhanced — full AI, optional website & documents</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {editType !== chatbot.type
                    ? "Changing the type unlocks different features. Click \"Save Changes\" to apply — the available knowledge sources and tools will update accordingly."
                    : "Determines which features are available: website crawling, document uploads, or full AI capabilities."}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="input-edit-scope">Scope Description</Label>
                <Input id="input-edit-scope" value={editScopeDescription} onChange={(e) => setEditScopeDescription(e.target.value)} placeholder="e.g., Customer support for acme.com" data-testid="input-edit-scope" />
                <p className="text-xs text-muted-foreground">Describe what this bot covers. The bot will restrict responses to this scope.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="textarea-edit-prompt">System Prompt</Label>
                <Textarea id="textarea-edit-prompt" value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} className="min-h-[120px] resize-none" data-testid="textarea-edit-prompt" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="input-edit-welcome">Welcome Message</Label>
                  <Input id="input-edit-welcome" value={editWelcome} onChange={(e) => setEditWelcome(e.target.value)} data-testid="input-edit-welcome" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="input-edit-end-message">End of Chat Message</Label>
                  <Input id="input-edit-end-message" value={editEndMessage} onChange={(e) => setEditEndMessage(e.target.value)} data-testid="input-edit-end-message" />
                  <p className="text-xs text-muted-foreground">Shown when the user ends the conversation, along with the rating prompt.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-500" />
                    Supervisor Agent
                  </CardTitle>
                  <CardDescription className="mt-1">An AI specialist that the bot can invoke during conversations for expert-level guidance</CardDescription>
                </div>
                <Switch
                  checked={editSupervisorEnabled}
                  onCheckedChange={setEditSupervisorEnabled}
                  aria-label="Enable supervisor agent"
                  data-testid="switch-supervisor-enabled"
                />
              </div>
            </CardHeader>
            {editSupervisorEnabled && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="textarea-supervisor-prompt">Supervisor Characteristics & Prompt</Label>
                  <Textarea
                    id="textarea-supervisor-prompt"
                    value={editSupervisorPrompt}
                    onChange={(e) => setEditSupervisorPrompt(e.target.value)}
                    className="min-h-[180px] resize-none"
                    placeholder="Define the supervisor agent's expertise, personality, and behavior. For example: 'You are a Recruitment Specialist with deep expertise in commercial and government staffing...'"
                    data-testid="textarea-supervisor-prompt"
                  />
                  <p className="text-xs text-muted-foreground">
                    The supervisor agent will be called by the bot when specialized expertise is needed during a conversation.
                    The supervisor's responses appear seamlessly within the bot chat — the user sees a more knowledgeable, in-depth response.
                    This agent counts toward your plan's agent limit.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Type className="w-5 h-5" />
                Voice &amp; Inclusivity
              </CardTitle>
              <CardDescription className="mt-1">
                Controls how this bot applies your organization's Institutional Voice &amp; Inclusivity profile.
                Manage the org-wide standards in Settings → Voice &amp; Inclusivity.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="select-voice-mode">Voice mode</Label>
                <Select value={editVoiceMode} onValueChange={setEditVoiceMode}>
                  <SelectTrigger id="select-voice-mode" data-testid="select-voice-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit — use the organization profile</SelectItem>
                    <SelectItem value="extend">Extend — org profile plus the additions below</SelectItem>
                    <SelectItem value="override">Override — use only the fields below</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {editVoiceMode === "inherit"
                    ? "This bot follows the organization's voice profile exactly."
                    : editVoiceMode === "extend"
                      ? "The fields below are appended on top of the organization's voice profile."
                      : "The organization's voice profile is ignored; only the fields below apply (blank fields fall back to institutional defaults)."}
                </p>
              </div>
              {editVoiceMode !== "inherit" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="textarea-voice-tone">Voice &amp; tone</Label>
                    <Textarea id="textarea-voice-tone" value={editVoiceTone} onChange={(e) => setEditVoiceTone(e.target.value)} className="min-h-[72px] resize-none" data-testid="textarea-voice-tone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="textarea-voice-preferred">Preferred terminology</Label>
                    <Textarea id="textarea-voice-preferred" value={editVoicePreferredTerms} onChange={(e) => setEditVoicePreferredTerms(e.target.value)} className="min-h-[72px] resize-none" data-testid="textarea-voice-preferred" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="textarea-voice-prohibited">Prohibited / non-inclusive terms</Label>
                    <Textarea id="textarea-voice-prohibited" value={editVoiceProhibitedTerms} onChange={(e) => setEditVoiceProhibitedTerms(e.target.value)} className="min-h-[72px] resize-none" data-testid="textarea-voice-prohibited" />
                    <p className="text-xs text-muted-foreground">Comma- or newline-separated. Any appearing in a response triggers an automatic inclusive rewrite.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="textarea-voice-reading">Reading level</Label>
                    <Textarea id="textarea-voice-reading" value={editVoiceReadingLevel} onChange={(e) => setEditVoiceReadingLevel(e.target.value)} className="min-h-[56px] resize-none" data-testid="textarea-voice-reading" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="textarea-voice-inclusivity">Inclusive-language standard</Label>
                    <Textarea id="textarea-voice-inclusivity" value={editVoiceInclusivityStandard} onChange={(e) => setEditVoiceInclusivityStandard(e.target.value)} className="min-h-[72px] resize-none" data-testid="textarea-voice-inclusivity" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Model Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="select-edit-model">Model</Label>
                {models.length > 0 ? (
                  <Select value={editModel} onValueChange={setEditModel}>
                    <SelectTrigger id="select-edit-model" data-testid="select-edit-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {allProviderModels && allProviderModels.length > 0 ? (
                        allProviderModels.map((pm) => (
                          <div key={pm.provider}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{pm.providerName}</div>
                            {pm.models.filter(m => isChatModel(pm.provider, m)).map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </div>
                        ))
                      ) : (
                        models.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                    <p className="text-sm text-amber-800 dark:text-amber-200" data-testid="text-no-models">
                      {hasApiKey
                        ? "No models detected. Go to Settings and click \"Refresh Available Models\" to re-detect."
                        : "Please add an AI provider API key in Settings first to see available models."}
                    </p>
                    <Link href="/settings">
                      <Button variant="ghost" size="sm" className="px-0 h-auto text-amber-700 dark:text-amber-300">Go to Settings</Button>
                    </Link>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Temperature: {editTemp / 10}</Label>
                <Slider value={[editTemp]} onValueChange={([v]) => setEditTemp(v)} min={0} max={10} step={1} aria-label="Temperature" data-testid="slider-edit-temperature" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="input-edit-max-tokens">Max Tokens</Label>
                <Input id="input-edit-max-tokens" type="number" value={editMaxTokens} onChange={(e) => setEditMaxTokens(parseInt(e.target.value) || 2048)} data-testid="input-edit-max-tokens" />
              </div>
              <div className="flex items-start gap-3 pt-2 border-t">
                <Switch
                  id="switch-allow-general-knowledge"
                  checked={editAllowGeneralKnowledge}
                  onCheckedChange={setEditAllowGeneralKnowledge}
                  data-testid="switch-allow-general-knowledge"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="switch-allow-general-knowledge" className="cursor-pointer">
                    Allow AI General Knowledge Fallback
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the chatbot can answer general questions using AI knowledge when no knowledge source is available.
                    Company-specific questions still require a knowledge base or tool — the AI will not invent internal policies, salaries, or proprietary data.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} data-testid="button-save-settings">
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
            <Button variant="destructive" onClick={() => { if (confirm("Archive this chatbot? It will no longer be active, but its conversations and history are retained.")) deleteMutation.mutate(); }} data-testid="button-delete-bot">
              <Trash2 className="w-4 h-4 mr-1" />Archive
            </Button>
          </div>
        </div>}

        {activeSection === "knowledge" && <KnowledgePanel chatbot={chatbot} />}

        {activeSection === "questions" && <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><HelpCircle className="w-5 h-5" />Predefined Questions</CardTitle>
              <CardDescription>Add suggested questions for this bot. Type any category name to define your own "areas of inquiry" — public visitors pick an area first, then a question. Categories are per-bot; nothing is fixed.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Input placeholder="e.g., What are your business hours?" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newQuestion.trim() && addQuestionMutation.mutate()} className="flex-1 min-w-[11rem]" data-testid="input-new-question" />
                <Input
                  list="bot-question-categories"
                  placeholder="Category"
                  value={newQuestionCategory}
                  onChange={(e) => setNewQuestionCategory(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && newQuestion.trim() && addQuestionMutation.mutate()}
                  className="w-[160px] max-w-full"
                  data-testid="input-question-category"
                />
                <datalist id="bot-question-categories">
                  {questionCategories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <Button onClick={() => addQuestionMutation.mutate()} disabled={!newQuestion.trim() || addQuestionMutation.isPending} data-testid="button-add-question">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
              {predefinedQuestions && predefinedQuestions.length > 0 && Object.keys(groupedQuestions).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground mr-1">Filter:</span>
                  <button
                    type="button"
                    onClick={() => setQuestionFilter("all")}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${questionFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                    data-testid="filter-category-all"
                  >
                    All ({predefinedQuestions.length})
                  </button>
                  {Object.entries(groupedQuestions).map(([cat, qs]) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setQuestionFilter(cat)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${questionFilter === cat ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                      data-testid={`filter-category-${cat}`}
                    >
                      {cat} ({qs.length})
                    </button>
                  ))}
                </div>
              )}
              {predefinedQuestions && predefinedQuestions.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupedQuestions)
                    .filter(([cat]) => questionFilter === "all" || cat === questionFilter)
                    .map(([cat, qs]) => (
                    <div key={cat} data-testid={`question-group-${cat}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                        {cat} <span className="text-muted-foreground/60">({qs.length})</span>
                      </div>
                      <div className="space-y-2">
                        {qs.map((q: any) => (
                          <div key={q.id} className="flex items-center justify-between gap-2 p-3 rounded-md bg-muted/50" data-testid={`question-${q.id}`}>
                            <p className="text-sm min-w-0 break-words">{q.question}</p>
                            <IconButton variant="ghost" label={`Archive question: ${q.question}`} onClick={() => deleteQuestionMutation.mutate(q.id)} data-testid={`button-delete-question-${q.id}`}>
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No predefined questions yet. Add some above.</p>
              )}
              {predefinedQuestions && predefinedQuestions.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {predefinedQuestions.map((q: any) => (
                      <span key={q.id} className="px-3 py-1.5 rounded-full text-xs border cursor-default bg-background hover:bg-muted transition-colors">{q.question}</span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>}

        {activeSection === "appearance" && <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Monitor className="w-5 h-5" />Display Mode</CardTitle>
                  <CardDescription>Choose how the chatbot appears to your users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button type="button" onClick={() => setEditDisplayMode("widget")}
                      className={`feature-tile text-left ${editDisplayMode === "widget" ? "ring-2 ring-primary border-primary" : ""}`}
                      data-testid="button-mode-widget"
                    >
                      <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
                        <MessageSquare className="w-4 h-4" />
                      </div>
                      <div className="font-medium text-sm">Chat Widget</div>
                      <p className="text-xs text-muted-foreground mt-1">Floating chat bubble on your website. Visitors click to open the chat window.</p>
                    </button>
                    <button type="button" onClick={() => setEditDisplayMode("fullscreen")}
                      className={`feature-tile text-left ${editDisplayMode === "fullscreen" ? "ring-2 ring-primary border-primary" : ""}`}
                      data-testid="button-mode-fullscreen"
                    >
                      <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(262 70% 60%), hsl(280 70% 55%))" }}>
                        <Monitor className="w-4 h-4" />
                      </div>
                      <div className="font-medium text-sm">Full Screen</div>
                      <p className="text-xs text-muted-foreground mt-1">ChatGPT-style full-page experience with conversation history sidebar.</p>
                    </button>
                  </div>
                  {editDisplayMode === "fullscreen" && (
                    <div className="mt-3 p-3 rounded-md bg-muted/50 border">
                      <p className="text-xs text-muted-foreground mb-2">Share this link to give users the full-screen chat experience:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-background px-2 py-1 rounded border flex-1 truncate" data-testid="text-fullscreen-url">
                          {window.location.origin}/chat/{chatbot.apiKey}
                        </code>
                        <CopyButton text={`${window.location.origin}/chat/${chatbot.apiKey}`} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><User className="w-5 h-5" />Who can use this bot</CardTitle>
                  <CardDescription>Choose whether end-users must sign in before they can ask the bot anything</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setEditAccessMode("authenticated")}
                      className={`p-4 rounded-lg border text-left transition-all ${editAccessMode === "authenticated" || editAccessMode === "login_required" ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                      data-testid="button-access-authenticated"
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <User className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="font-medium text-sm">Login required</span>
                        <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">recommended</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Only signed-in members of your organisation can chat. Tools and personal data are scoped to the caller's role.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditAccessMode("public")}
                      className={`p-4 rounded-lg border text-left transition-all ${editAccessMode === "public" ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                      data-testid="button-access-public"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        <span className="font-medium text-sm">Public</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Anyone with the embed widget can chat anonymously. Domain restrictions apply. Tools that need a user identity stay disabled.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditAccessMode("api_key_only")}
                      className={`p-4 rounded-lg border text-left transition-all ${editAccessMode === "api_key_only" ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                      data-testid="button-access-api-key-only"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Code className="w-5 h-5 text-primary" />
                        <span className="font-medium text-sm">API key only</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Server-to-server calls using the bot API key. No domain validation, no user login required. Intended for backend integrations.
                      </p>
                    </button>
                  </div>
                  {(editAccessMode === "authenticated" || editAccessMode === "login_required") && (
                    <div className="mt-3 p-3 rounded-md bg-muted/50 border text-xs text-muted-foreground">
                      Visitors who open the chat link without a session will be sent to your organisation's sign-in page first.
                    </div>
                  )}
                  {editAccessMode === "api_key_only" && (
                    <div className="mt-3 p-3 rounded-md bg-muted/50 border text-xs text-muted-foreground">
                      Callers must include <code className="font-mono bg-muted px-1 rounded">Authorization: Bearer &lt;bot-api-key&gt;</code> in every request. No user login or domain allow-list is enforced — suitable for trusted back-end services only.
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-sm">Pre-chat contact form</div>
                      <p className="text-xs text-muted-foreground mt-1 max-w-md">
                        Ask anonymous visitors for their name, email, and phone number (with a data-collection
                        consent notice) before they can start chatting. Captured as a lead tied to the conversation.
                        Signed-in users skip the form.
                      </p>
                      {editAccessMode === "public" && (
                        <p className="text-xs font-medium text-primary mt-2" data-testid="text-prechat-public-note">
                          Always on for public chatbots — every visitor provides contact details before chatting.
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={editAccessMode === "public" ? true : editPreChatForm}
                      onCheckedChange={setEditPreChatForm}
                      disabled={editAccessMode === "public"}
                      data-testid="switch-prechat-form"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Palette className="w-5 h-5" />Theme</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2">
                    {BOT_THEMES.map((theme) => (
                      <button key={theme.id} type="button" onClick={() => { setEditTheme(theme.id); setEditColor(theme.primaryColor); }}
                        className={`p-3 rounded-md border text-left transition-all ${editTheme === theme.id ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                        data-testid={`button-theme-${theme.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: theme.primaryColor }} />
                          <span className="text-xs font-medium truncate">{theme.name}</span>
                        </div>
                        <div className="flex gap-1">
                          <div className="w-full h-2 rounded-full" style={{ backgroundColor: theme.headerBg }} />
                        </div>
                        <div className="flex gap-1 mt-1">
                          <div className="flex-1 h-2 rounded-sm" style={{ backgroundColor: theme.userBubble }} />
                          <div className="flex-1 h-2 rounded-sm" style={{ backgroundColor: theme.botBubble }} />
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color-primary">Primary Color Override</Label>
                    <div className="flex items-center gap-3">
                      <input id="color-primary" type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="w-10 h-10 rounded-md border cursor-pointer" data-testid="color-primary" />
                      <Input value={editColor} onChange={(e) => setEditColor(e.target.value)} className="max-w-[140px]" aria-label="Primary color hex value" data-testid="input-primary-color" />
                    </div>
                  </div>

                  <div className="pt-2 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Background &amp; Font Colors</Label>
                      {(editBackgroundColor || editBotBubbleColor || editBotTextColor || editUserBubbleColor || editUserTextColor) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setEditBackgroundColor("");
                            setEditBotBubbleColor("");
                            setEditBotTextColor("");
                            setEditUserBubbleColor("");
                            setEditUserTextColor("");
                          }}
                          data-testid="button-reset-colors"
                        >
                          Reset to theme
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Override individual colors. Leave a swatch on its theme value to inherit from the theme above.
                    </p>
                    {[
                      { label: "Chat Background", value: editBackgroundColor, set: setEditBackgroundColor, fallback: currentTheme.backgroundColor, testid: "background" },
                      { label: "Bot Message Background", value: editBotBubbleColor, set: setEditBotBubbleColor, fallback: currentTheme.botBubble, testid: "bot-bubble" },
                      { label: "Bot Message Font", value: editBotTextColor, set: setEditBotTextColor, fallback: currentTheme.botBubbleText, testid: "bot-text" },
                      { label: "User Message Background", value: editUserBubbleColor, set: setEditUserBubbleColor, fallback: currentTheme.userBubble, testid: "user-bubble" },
                      { label: "User Message Font", value: editUserTextColor, set: setEditUserTextColor, fallback: currentTheme.userBubbleText, testid: "user-text" },
                    ].map(({ label, value, set, fallback, testid }) => (
                      // Below 360px the label stacks above the swatch+hex pair
                      // so it never squeezes into a 3-line sliver.
                      <div key={testid} className="flex flex-wrap items-center gap-2">
                        <Label htmlFor={`color-${testid}`} className="text-xs text-muted-foreground w-full min-[360px]:w-auto min-[360px]:flex-1">{label}</Label>
                        <input
                          id={`color-${testid}`}
                          type="color"
                          value={value || fallback}
                          onChange={(e) => set(e.target.value)}
                          className="w-9 h-9 rounded-md border cursor-pointer"
                          data-testid={`color-${testid}`}
                        />
                        <Input
                          value={value}
                          placeholder={fallback}
                          onChange={(e) => set(e.target.value)}
                          className="w-[110px] font-mono text-xs"
                          aria-label={`${label} hex value`}
                          data-testid={`input-color-${testid}`}
                        />
                      </div>
                    ))}
                    {(() => {
                      // WCAG 1.4.3 admin feedback: warn when a chosen font color
                      // will be adjusted at runtime to reach 4.5:1 contrast.
                      const { adjusted } = resolveThemeWithContrast({
                        theme: editTheme,
                        primaryColor: editColor,
                        backgroundColor: editBackgroundColor || null,
                        botBubbleColor: editBotBubbleColor || null,
                        botTextColor: editBotTextColor || null,
                        userBubbleColor: editUserBubbleColor || null,
                        userTextColor: editUserTextColor || null,
                      });
                      if (!adjusted.botText && !adjusted.userText) return null;
                      const parts = [
                        adjusted.botText ? "bot message font" : null,
                        adjusted.userText ? "user message font" : null,
                      ].filter(Boolean);
                      return (
                        <p role="status" className="text-xs text-amber-700 dark:text-amber-400" data-testid="text-contrast-adjusted">
                          Low contrast: the {parts.join(" and ")} color will be automatically
                          adjusted in chat to stay readable (WCAG 4.5:1). Pick a
                          darker/lighter font color to keep your exact choice.
                        </p>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bot Icon</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {iconOptions.map((opt) => (
                      <button key={opt.value} type="button" onClick={() => setEditBotIcon(opt.value)}
                        className={`p-3 rounded-md border flex flex-col items-center gap-1 w-16 transition-all ${editBotIcon === opt.value ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                        data-testid={`button-icon-${opt.value}`}
                      >
                        <opt.icon className="w-5 h-5" />
                        <span className="text-[9px] text-muted-foreground">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} data-testid="button-save-appearance">
                {updateMutation.isPending ? "Saving..." : "Save Appearance"}
              </Button>
            </div>

            <div className="sticky top-20 space-y-4">
              <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">Live Preview</p>
                <span className="brand-pill text-[10px] px-2 py-0.5 rounded-full">
                  {editDisplayMode === "fullscreen" ? "Full Screen" : "Widget"}
                </span>
              </div>
              <div className="dot-grid rounded-2xl border border-card-border p-5 sm:p-7 flex items-center justify-center">
              {editDisplayMode === "fullscreen" ? (
                <div className="w-full rounded-xl overflow-hidden shadow-lg border border-slate-200 bg-slate-50" style={{ minHeight: "380px" }}>
                  <div className="flex h-[380px]">
                    {/* Sidebar */}
                    <div className="w-[140px] border-r border-slate-200 bg-white flex flex-col">
                      <div className="px-2 pt-2 pb-1.5 flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded flex items-center justify-center" style={{ backgroundColor: editColor || currentTheme.primaryColor, color: readableTextOn(editColor || currentTheme.primaryColor) }}>
                          <Bot className="w-2.5 h-2.5" />
                        </div>
                        <span className="text-[8px] font-semibold text-slate-700 truncate">{editName || "Your Bot"}</span>
                      </div>
                      <div className="px-1.5 space-y-0.5">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 text-[8px] text-slate-700 font-medium">
                          <Sparkles className="w-2.5 h-2.5 text-emerald-500" /> AI Chat
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[8px] text-slate-500">
                          <MessageSquare className="w-2.5 h-2.5 text-slate-400" /> Documents
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[8px] text-slate-500">
                          <MessageSquare className="w-2.5 h-2.5 text-slate-400" /> History
                        </div>
                      </div>
                      <div className="px-1.5 pt-1.5">
                        <div className="flex items-center justify-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-[8px] text-slate-600">
                          <Plus className="w-2.5 h-2.5" /> New chat
                        </div>
                      </div>
                      <div className="px-2 pt-2 text-[7px] uppercase tracking-wider text-slate-400">Recent</div>
                      <div className="flex-1 px-1.5 space-y-0.5 overflow-hidden">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[8px] text-slate-500 truncate">
                          <MessageSquare className="w-2.5 h-2.5 text-slate-400" /> Tell me about…
                        </div>
                      </div>
                    </div>
                    {/* Main column */}
                    <div className="flex-1 flex flex-col min-w-0 bg-slate-50">
                      <div className="h-7 flex items-center px-3 border-b border-slate-200 bg-white">
                        <span className="text-[9px] font-semibold text-slate-700">AI Chat</span>
                      </div>
                      <div className="flex-1 overflow-hidden flex flex-col items-center justify-center px-3 text-center">
                        <div className="relative mb-2">
                          <div className="absolute inset-0 rounded-full bg-emerald-300 blur-xl opacity-60" />
                          <div className="relative w-10 h-10 rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, #d9f99d 0%, #4ade80 40%, #16a34a 75%, #064e3b 100%)" }} />
                        </div>
                        <div className="text-[10px] font-semibold text-slate-800">Welcome to {editName || "Your Bot"}</div>
                        <p className="text-[8px] text-slate-500 mt-0.5 max-w-[180px] leading-snug">{editWelcome || "Get started by giving me a task."}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 mt-2 w-full max-w-[220px]">
                          <div className="rounded-md border border-slate-200 bg-white p-1">
                            <div className="w-3 h-3 rounded bg-sky-100 flex items-center justify-center mb-0.5">
                              <Zap className="w-2 h-2 text-sky-600" />
                            </div>
                            <div className="text-[7px] font-semibold leading-tight">Productivity</div>
                          </div>
                          <div className="rounded-md border border-slate-200 bg-white p-1">
                            <div className="w-3 h-3 rounded bg-amber-100 flex items-center justify-center mb-0.5">
                              <Smile className="w-2 h-2 text-amber-600" />
                            </div>
                            <div className="text-[7px] font-semibold leading-tight">Onboarding</div>
                          </div>
                          <div className="rounded-md border border-slate-200 bg-white p-1">
                            <div className="w-3 h-3 rounded bg-rose-100 flex items-center justify-center mb-0.5">
                              <Volume2 className="w-2 h-2 text-rose-600" />
                            </div>
                            <div className="text-[7px] font-semibold leading-tight">Voice</div>
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-2.5 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] text-slate-400 flex-1">Ask me anything…</span>
                            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: editColor || currentTheme.primaryColor, color: readableTextOn(editColor || currentTheme.primaryColor) }}>
                              <Send className="w-2.5 h-2.5" />
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-1 text-[7px] text-slate-400">
                            <div className="flex items-center gap-1.5">
                              <span>Attach</span>
                              <span>Voice</span>
                              <span>Prompts</span>
                            </div>
                            <span>0/1500</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full max-w-md rounded-xl overflow-hidden shadow-lg border" style={{ borderRadius: previewTheme.borderRadius, fontFamily: previewTheme.fontFamily }}>
                  <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: previewTheme.headerBg, color: previewTheme.headerText }}>
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: previewTheme.primaryColor, color: readableTextOn(previewTheme.primaryColor) }}>
                      {(() => {
                        const IconComp = iconOptions.find(o => o.value === editBotIcon)?.icon || Bot;
                        return <IconComp className="w-4 h-4" />;
                      })()}
                    </div>
                    <span className="text-sm font-semibold">{editName || "Your Bot"}</span>
                  </div>
                  <div className="p-4 space-y-3" style={{ backgroundColor: previewTheme.backgroundColor, minHeight: "250px" }}>
                    <div className="flex gap-2">
                      <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: previewTheme.primaryColor, color: readableTextOn(previewTheme.primaryColor) }}>
                        <Bot className="w-3 h-3" />
                      </div>
                      <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: previewTheme.botBubble, color: previewTheme.botBubbleText, borderRadius: previewTheme.borderRadius }}>
                        {editWelcome || "Hello! How can I help you today?"}
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: previewTheme.userBubble, color: previewTheme.userBubbleText, borderRadius: previewTheme.borderRadius }}>
                        Tell me about your services
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: previewTheme.primaryColor, color: readableTextOn(previewTheme.primaryColor) }}>
                        <Bot className="w-3 h-3" />
                      </div>
                      <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: previewTheme.botBubble, color: previewTheme.botBubbleText, borderRadius: previewTheme.borderRadius }}>
                        I'd be happy to help you learn about our services!
                      </div>
                    </div>
                  </div>
                  <div className="p-3 border-t flex gap-2" style={{ backgroundColor: previewTheme.backgroundColor }}>
                    <div className="flex-1 px-3 py-1.5 rounded-md text-xs border" style={{ borderColor: previewTheme.primaryColor + "40" }}>Type a message...</div>
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: previewTheme.primaryColor, color: readableTextOn(previewTheme.primaryColor) }}>
                      <Send className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              )}
              </div>
              </div>
              <KnowledgeSourcesCard chatbot={chatbot} onJump={setActiveSection} />
              <ChannelsIntegrationsCard chatbot={chatbot} onJump={setActiveSection} />
            </div>
          </div>
        </div>}

        {activeSection === "domains" && <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Shield className="w-5 h-5" />Domain Restrictions</CardTitle>
              <CardDescription>Control which websites are allowed to embed this chatbot. If no domains are listed, the widget will work on any domain.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="textarea-edit-domains">Allowed Domains</Label>
                <Textarea id="textarea-edit-domains" placeholder={"example.com\napp.example.com\nshop.example.com"} value={editDomains} onChange={(e) => setEditDomains(e.target.value)} className="min-h-[120px] resize-none font-mono text-sm" data-testid="textarea-edit-domains" />
                <p className="text-xs text-muted-foreground">One domain per line. The embed widget will validate the hosting domain before initializing. Leave empty to allow all domains.</p>
              </div>
              <div className="p-4 rounded-md bg-muted/50">
                <h4 className="text-sm font-medium mb-2">Current Allowed Domains</h4>
                {(chatbot.allowedDomains && chatbot.allowedDomains.length > 0) ? (
                  <div className="flex flex-wrap gap-2">
                    {chatbot.allowedDomains.map((d, i) => (
                      <Badge key={i} variant="outline" className="font-mono text-xs" data-testid={`badge-domain-${i}`}>{d}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No domain restrictions — widget works on all domains.</p>
                )}
              </div>
              <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} data-testid="button-save-domains">
                {updateMutation.isPending ? "Saving..." : "Save Domain Settings"}
              </Button>
            </CardContent>
          </Card>
        </div>}

        {activeSection === "access" && <BotAccessPanel chatbotId={chatbot.id} />}

        {activeSection === "channels" && <ChannelsPanel chatbot={chatbot} />}

        {activeSection === "ratings" && <RatingsPanel chatbotId={chatbot.id} />}

        {activeSection === "reports" && <ReportsPanel chatbot={chatbot} />}

        {activeSection === "live-chat" && <LiveChatPanel chatbot={chatbot} />}

        {activeSection === "webhooks" && <WebhooksPanel chatbotId={chatbot.id} />}

        {activeSection === "preview" && (
          chatbot.displayMode === "fullscreen" ? (
            <div className="space-y-6">
              {/* Always render the admin ChatPreview first so the
                  "Preview as" impersonation picker is available even when
                  the bot's end-user mode is fullscreen. The fullscreen
                  iframe stays below as a visual reference. */}
              <ChatPreview chatbot={chatbot} />
              <div className="max-w-4xl mx-auto">
                <p className="text-xs text-muted-foreground text-center mb-2">
                  Full-screen mode preview — this is what end-users will see when they visit the chat link.
                  Use the panel above (with "Preview as") to actually test the bot.
                </p>
                <div className="rounded-lg overflow-hidden border shadow-lg" style={{ height: "480px" }}>
                  <iframe
                    src={`/chat/${chatbot.apiKey}`}
                    className="w-full h-full border-0"
                    title="Fullscreen Chat Preview"
                    data-testid="iframe-fullscreen-preview"
                  />
                </div>
              </div>
            </div>
          ) : (
            <ChatPreview chatbot={chatbot} />
          )
        )}

        {activeSection === "api" && <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Key</CardTitle>
              <CardDescription>Use this key to authenticate API requests</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 p-3 rounded-md bg-muted font-mono text-sm">
                <code className="flex-1 truncate" data-testid="text-api-key">{chatbot.apiKey}</code>
                <CopyButton text={chatbot.apiKey} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Endpoint</CardTitle>
              <CardDescription>Send chat messages to this endpoint</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 p-3 rounded-md bg-muted font-mono text-sm">
                <code className="flex-1 truncate">POST {apiEndpoint}</code>
                <CopyButton text={`POST ${apiEndpoint}`} />
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Example Request (cURL)</h4>
                <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`curl -X POST ${apiEndpoint} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${chatbot.apiKey}" \\
  -d '{
    "message": "Hello!",
    "session_id": "unique-session-id",
    "channel": "web"
  }'`}
                </pre>
                <CopyButton text={`curl -X POST ${apiEndpoint} \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Bearer ${chatbot.apiKey}" \\\n  -d '{\n    "message": "Hello!",\n    "session_id": "unique-session-id"\n  }'`} />
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Example Request (JavaScript)</h4>
                <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`const response = await fetch("${apiEndpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${chatbot.apiKey}"
  },
  body: JSON.stringify({
    message: "Hello!",
    session_id: "unique-session-id",
    channel: "web" // options: web, sms, email, mobile, api
  })
});

const data = await response.json();
console.log(data.reply);`}
                </pre>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Response Format</h4>
                <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap">
{`{
  "reply": "The AI response message",
  "conversation_id": "uuid",
  "session_id": "your-session-id",
  "channel": "web"
}`}
                </pre>
              </div>
            </CardContent>
          </Card>

            <>
              <Card className="border-primary/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Monitor className="w-5 h-5" />Full-Screen Chat URL</CardTitle>
                  <CardDescription>Share this link directly — users get a ChatGPT-style full-page experience</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <code className="text-sm bg-muted px-3 py-2 rounded-md border flex-1 truncate" data-testid="text-fullscreen-url-embed">
                      {window.location.origin}/chat/{chatbot.apiKey}
                    </code>
                    <CopyButton text={`${window.location.origin}/chat/${chatbot.apiKey}`} />
                  </div>
                  <a href={`/chat/${chatbot.apiKey}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="link-preview-fullscreen">
                    <Eye className="w-3 h-3" /> Preview full-screen chat
                  </a>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Iframe Embed</CardTitle>
                  <CardDescription>Embed the full chatbot in SharePoint or another website, regardless of display mode. SharePoint may require an administrator to allow this host in HTML Field Security. Public bots use the existing consent form; restricted bots still require sign-in.</CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{iframeEmbedCode(window.location.origin, chatbot.apiKey, chatbot.name)}
                  </pre>
                  <CopyButton text={iframeEmbedCode(window.location.origin, chatbot.apiKey, chatbot.name)} />
                </CardContent>
              </Card>
            </>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">JavaScript Widget</CardTitle>
                <CardDescription>Add this snippet to your website to embed the chatbot</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`<script>
  (function() {
    var s = document.createElement('script');
    s.src = '${window.location.origin}/embed.js';
    s.setAttribute('data-api-key', '${chatbot.apiKey}');
    document.body.appendChild(s);
  })();
</script>`}
                </pre>
                <CopyButton text={`<script>\n  (function() {\n    var s = document.createElement('script');\n    s.src = '${window.location.origin}/embed.js';\n    s.setAttribute('data-api-key', '${chatbot.apiKey}');\n    document.body.appendChild(s);\n  })();\n</script>`} />
              </CardContent>
            </Card>
          {/* ── External Authentication documentation ─────────────────────── */}
          {/* Developer reference: how to pass an external IdP token at chat   */}
          {/* time. Always shown in the API tab so devs can read it without    */}
          {/* needing to navigate to the SSO settings page.                    */}
          <Card data-testid="card-external-auth-docs">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                External Authentication
              </CardTitle>
              <CardDescription>
                Allow users authenticated in an external system (LMS, portal, enterprise app) to
                chat without a separate platform login. Requires External SSO to be configured in{" "}
                <strong>Settings → Identity &amp; SSO</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* How it works */}
              <div className="space-y-1.5">
                <h4 className="text-sm font-medium">How it works</h4>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Your application obtains a JWT from its Identity Provider (OIDC) or a SAML 2.0 assertion.</li>
                  <li>Pass the token as the <code className="bg-muted px-1 rounded">X-External-Token</code> header alongside the bot's API key.</li>
                  <li>The platform validates the token against the configured external IdP and extracts the user's identity.</li>
                  <li>The user's role, department, and groups determine which knowledge sources they can access.</li>
                </ol>
              </div>

              {/* Required claims */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Required &amp; recommended JWT claims</h4>
                <div className="rounded-md border overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Default claim name</th>
                        <th className="text-left px-3 py-2 font-medium">Field</th>
                        <th className="text-left px-3 py-2 font-medium">Required</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {[
                        { claim: "sub",        field: "User ID",    req: "Yes" },
                        { claim: "email",      field: "Email",      req: "Recommended" },
                        { claim: "name",       field: "Display name", req: "Optional" },
                        { claim: "roles",      field: "Role",       req: "Optional — for KS access control" },
                        { claim: "department", field: "Department", req: "Optional — for KS access control" },
                        { claim: "groups",     field: "Groups",     req: "Optional — for KS access control" },
                      ].map(r => (
                        <tr key={r.claim} className="hover:bg-muted/40">
                          <td className="px-3 py-1.5 font-mono">{r.claim}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.field}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.req}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Claim names can be remapped in <strong>Settings → Identity &amp; SSO → External SSO → User Identity Mapping</strong>.
                  For SAML 2.0, <code className="bg-muted px-1 rounded">sub</code> maps to the SAML <code className="bg-muted px-1 rounded">NameID</code>.
                </p>
              </div>

              {/* API example */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Example — direct API call</h4>
                <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`const externalToken = await getTokenFromYourIdP(); // JWT from your OIDC IdP

const response = await fetch("${apiEndpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${chatbot.apiKey}",  // Bot API key
    "X-External-Token": externalToken             // User's IdP JWT
  },
  body: JSON.stringify({
    message: "Hello!",
    session_id: crypto.randomUUID(),
    channel: "web"
  })
});

const data = await response.json();
console.log(data.reply);`}
                </pre>
              </div>

              {/* Widget embed example */}
              {chatbot.displayMode !== "fullscreen" && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Example — embed widget with external token</h4>
                  <pre className="p-4 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`<script>
  (function() {
    // 1. Get token from your Identity Provider
    var externalToken = getTokenFromYourIdP(); // returns a JWT string

    // 2. Load the embed widget with the external token
    var s = document.createElement('script');
    s.src = '${window.location.origin}/embed.js';
    s.setAttribute('data-api-key', '${chatbot.apiKey}');
    s.setAttribute('data-external-token', externalToken);
    document.body.appendChild(s);
  })();
</script>`}
                  </pre>
                  <p className="text-xs text-muted-foreground">
                    The <code className="bg-muted px-1 rounded">data-external-token</code> attribute is forwarded
                    as the <code className="bg-muted px-1 rounded">X-External-Token</code> header on every chat
                    request. Refresh the token and update the attribute before it expires.
                  </p>
                </div>
              )}

              {/* Error codes */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Common error codes</h4>
                <div className="rounded-md border overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Code</th>
                        <th className="text-left px-3 py-2 font-medium">Meaning</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {[
                        { code: "EXTERNAL_IDP_NOT_CONFIGURED", desc: "No external IdP is set up for this chatbot — configure it in Settings → Identity & SSO." },
                        { code: "EXTERNAL_TOKEN_INVALID",      desc: "Token signature, issuer, audience, or expiry failed validation." },
                        { code: "AUTH_REQUIRED",               desc: "The chatbot requires authentication but neither a platform login nor an external token was supplied." },
                      ].map(r => (
                        <tr key={r.code} className="hover:bg-muted/40">
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">{r.code}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </CardContent>
          </Card>

        </div>}
        </div>

        {/* Persistent right rail — hidden on the dedicated Preview tab
            because that view already shows the chat full-width, and on
            Appearance because the same two cards are stacked under the
            Live Preview in a single column there. */}
        {activeSection !== "preview" && activeSection !== "appearance" && (
          <BotRightRail chatbot={chatbot} onJump={setActiveSection} />
        )}
      </div>
    </div>
  );
}

function LiveChatPanel({ chatbot }: { chatbot: Chatbot }) {
  const { toast } = useToast();
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const { data: liveConvos, isLoading } = useQuery<any[]>({
    queryKey: ["/api/chatbots", chatbot.id, "live-conversations"],
    refetchInterval: 5000,
  });

  const { data: messages } = useQuery<any[]>({
    queryKey: ["/api/conversations", selectedConvo, "messages"],
    enabled: !!selectedConvo,
    refetchInterval: 3000,
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/conversations/${selectedConvo}/reply`, { message: replyText });
    },
    onSuccess: () => {
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["/api/conversations", selectedConvo, "messages"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (convoId: string) => {
      await apiRequest("POST", `/api/conversations/${convoId}/resolve`);
    },
    onSuccess: () => {
      setSelectedConvo(null);
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbot.id, "live-conversations"] });
      toast({ title: "Conversation resolved" });
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Headphones className="w-5 h-5" />
            Live Chat
          </CardTitle>
          <CardDescription>
            Respond to users who requested to speak with a representative
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !liveConvos || liveConvos.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Headphones className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No active handoff requests</p>
              <p className="text-sm mt-1">When users request to speak with a representative, they will appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  {liveConvos.length} active conversation{liveConvos.length !== 1 ? "s" : ""}
                </p>
                {liveConvos.map((convo: any) => (
                  <button
                    key={convo.id}
                    onClick={() => setSelectedConvo(convo.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedConvo === convo.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                    data-testid={`button-convo-${convo.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{convo.userName || "Visitor"}</span>
                      </div>
                      <Badge variant={convo.status === "handoff_requested" ? "destructive" : "default"} className="text-[10px]">
                        {convo.status === "handoff_requested" ? "Waiting" : "Active"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Session: {convo.sessionId?.substring(0, 12)}...
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {convo.createdAt ? new Date(convo.createdAt).toLocaleString() : ""}
                    </p>
                  </button>
                ))}
              </div>

              {selectedConvo && (
                <div className="lg:col-span-2 border rounded-lg flex flex-col" style={{ height: "500px" }}>
                  <div className="p-3 border-b flex items-center justify-between bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Headphones className="w-4 h-4" />
                      <span className="text-sm font-medium">Live Conversation</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resolveMutation.mutate(selectedConvo)}
                      disabled={resolveMutation.isPending}
                      data-testid="button-resolve-conversation"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" />
                      Resolve
                    </Button>
                  </div>

                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-3">
                      {messages?.map((msg: any, i: number) => (
                        <div
                          key={msg.id || i}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                              msg.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : msg.role === "agent"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 border border-green-200 dark:border-green-800"
                                : "bg-muted"
                            }`}
                            data-testid={`message-${msg.role}-${i}`}
                          >
                            {msg.role === "agent" && (
                              <div className="flex items-center gap-1 mb-1">
                                <Headphones className="w-3 h-3" />
                                <span className="text-[10px] font-semibold uppercase">Representative</span>
                              </div>
                            )}
                            {msg.role === "assistant" && (
                              <div className="flex items-center gap-1 mb-1">
                                <Bot className="w-3 h-3" />
                                <span className="text-[10px] font-semibold uppercase">Bot</span>
                              </div>
                            )}
                            <p>{msg.content}</p>
                            <p className="text-[10px] opacity-50 mt-1">
                              {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString() : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <div className="p-3 border-t">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (replyText.trim()) replyMutation.mutate();
                      }}
                      className="flex gap-2"
                    >
                      <Input
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type a reply..."
                        disabled={replyMutation.isPending}
                        data-testid="input-agent-reply"
                      />
                      <IconButton
                        type="submit"
                        label="Send reply"
                        disabled={!replyText.trim() || replyMutation.isPending}
                        data-testid="button-send-reply"
                      >
                        <Send className="w-4 h-4" aria-hidden="true" />
                      </IconButton>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WebhooksPanel({ chatbotId }: { chatbotId: string }) {
  const { toast } = useToast();
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState<string[]>(["message.received"]);
  // Ephemeral state only — never persisted to query cache, localStorage, or anywhere else.
  // Cleared when the copy-once dialog is dismissed.
  const [revealedSecret, setRevealedSecret] = useState<{ webhookId: string; secret: string } | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const { data: webhooksData, isLoading } = useQuery<any[]>({
    queryKey: ["/api/chatbots", chatbotId, "webhooks"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/webhooks?chatbotId=${chatbotId}`);
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/webhooks`, { chatbotId, url: newUrl, events: newEvents });
      return res.json();
    },
    onSuccess: (data: any) => {
      // Invalidate list (which never returns the secret).
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "webhooks"] });
      setNewUrl("");
      // Show the initial secret in the copy-once modal if the server returned it.
      if (data?.secret && data?.webhook?.id) {
        setRevealedSecret({ webhookId: data.webhook.id, secret: data.secret });
        setSecretCopied(false);
      } else {
        toast({ title: "Webhook created" });
      }
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      await apiRequest("DELETE", `/api/webhooks/${webhookId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "webhooks"] });
      toast({ title: "Webhook disconnected" });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      const res = await apiRequest("POST", `/api/webhooks/${webhookId}/test`);
      return res.json();
    },
    onSuccess: () => toast({ title: "Test webhook sent" }),
    onError: () => toast({ title: "Test failed", variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ webhookId, isActive }: { webhookId: string; isActive: boolean }) => {
      await apiRequest("PATCH", `/api/webhooks/${webhookId}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "webhooks"] });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      const res = await apiRequest("POST", `/api/webhooks/${webhookId}/rotate-secret`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? `Request failed (${res.status})`);
      }
      return res.json() as Promise<{ secret: string }>;
    },
    onSuccess: (data, webhookId) => {
      // DO NOT merge secret into query cache or any persistent state.
      // Show in ephemeral modal only; cleared on dismiss.
      setRevealedSecret({ webhookId, secret: data.secret });
      setSecretCopied(false);
      // Invalidate the list — it never returns the secret, so this is safe.
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "webhooks"] });
    },
    onError: (err: any) => toast({
      title: "Rotation failed",
      description: err.message,
      variant: "destructive",
    }),
  });

  const handleDismissSecret = () => {
    setRevealedSecret(null);
    setSecretCopied(false);
  };

  const handleCopySecret = () => {
    if (!revealedSecret) return;
    navigator.clipboard.writeText(revealedSecret.secret).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    });
  };

  const allEvents = ["message.received", "conversation.started", "conversation.ended", "rating.submitted"];

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-4" data-testid="webhooks-panel">

      {/* ── Copy-once secret reveal modal ─────────────────────────────────
          This is the ONLY place the secret is ever visible. It is held in
          ephemeral React state and cleared when the dialog closes.
          It is never written to React Query cache, localStorage, or any URL.
      ─────────────────────────────────────────────────────────────────── */}
      <Dialog open={revealedSecret !== null} onOpenChange={(open) => { if (!open) handleDismissSecret(); }}>
        <DialogContent data-testid="dialog-webhook-secret">
          <DialogHeader>
            <DialogTitle>Signing Secret</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-destructive">
                Copy this secret now — it will not be shown again.
              </span>{" "}
              Configure your webhook receiver to verify the{" "}
              <code className="text-xs bg-muted px-1 rounded">X-Webhook-Signature</code> header
              using HMAC-SHA256 with this value.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <code
              className="flex-1 text-xs bg-muted p-3 rounded break-all font-mono select-all"
              data-testid="text-revealed-webhook-secret"
            >
              {revealedSecret?.secret}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopySecret}
              data-testid="btn-copy-webhook-secret"
              aria-label="Copy secret to clipboard"
            >
              {secretCopied
                ? <><Check className="w-3 h-3 mr-1" />Copied</>
                : <><Copy className="w-3 h-3 mr-1" />Copy</>}
            </Button>
          </div>
          <DialogFooter className="mt-4">
            <Button onClick={handleDismissSecret} data-testid="btn-dismiss-webhook-secret">
              I've copied it — close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add Webhook</CardTitle>
          <CardDescription>Receive HTTP POST notifications when events occur</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="input-webhook-url">Endpoint URL</Label>
            <Input
              id="input-webhook-url"
              data-testid="input-webhook-url"
              placeholder="https://your-server.com/webhook"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
          </div>
          <div>
            <Label>Events</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {allEvents.map((evt) => (
                <Button
                  key={evt}
                  size="sm"
                  variant={newEvents.includes(evt) ? "default" : "outline"}
                  data-testid={`btn-event-${evt}`}
                  onClick={() =>
                    setNewEvents((prev) =>
                      prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]
                    )
                  }
                >
                  {evt}
                </Button>
              ))}
            </div>
          </div>
          <Button
            data-testid="btn-create-webhook"
            onClick={() => createMutation.mutate()}
            disabled={!newUrl || newEvents.length === 0 || createMutation.isPending}
          >
            <Plus className="w-4 h-4 mr-1" />
            {createMutation.isPending ? "Creating..." : "Add Webhook"}
          </Button>
        </CardContent>
      </Card>

      {webhooksData && webhooksData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Active Webhooks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {webhooksData.map((hook: any) => (
              <div
                key={hook.id}
                className="border rounded-lg p-3 space-y-2"
                data-testid={`webhook-item-${hook.id}`}
              >
                <div className="flex items-center justify-between">
                  <code className="text-sm bg-muted px-2 py-1 rounded" data-testid={`text-webhook-url-${hook.id}`}>
                    {hook.url}
                  </code>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`switch-webhook-${hook.id}`}
                      checked={!!hook.isActive}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ webhookId: hook.id, isActive: checked })
                      }
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-test-webhook-${hook.id}`}
                      onClick={() => testMutation.mutate(hook.id)}
                      disabled={testMutation.isPending}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Test
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-rotate-secret-${hook.id}`}
                      onClick={() => rotateMutation.mutate(hook.id)}
                      disabled={rotateMutation.isPending}
                      title="Rotate signing secret"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Rotate Secret
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      data-testid={`btn-delete-webhook-${hook.id}`}
                      onClick={() => deleteMutation.mutate(hook.id)}
                      title="Disconnect webhook"
                      aria-label="Disconnect webhook"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {hook.events?.map((evt: string) => (
                    <Badge key={evt} variant="secondary" className="text-xs">
                      {evt}
                    </Badge>
                  ))}
                </div>
                {/* Secret is never included in list responses and never stored in
                    hook state. The copy-once dialog above is the only reveal surface. */}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
